/**
 * 🚀 HERO HEADER 2025 - Effiziente Migration
 * 
 * Behält das EXAKTE ursprüngliche Design bei, nutzt aber:
 * - Neue LayoutEngine für Layout-Konfigurationen
 * - Optimierte Responsive-Behandlung
 * - Saubere Code-Struktur
 * - Alle ursprünglichen Features
 */

import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import './DynamicContainerSections.css';
import { useFont } from '../../Design/FontContext';
import { titleFonts, textFonts } from '../../utils/fontsHelper';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import { getButtonEffectStyles } from '../utils/buttonEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import HighlightText from '../WidgetEditComponents/HighlightText';
import LayoutEngine from '../../core/LayoutEngine';
import { renderSafeRichText } from '../utils/richTextUtils';
import { isLivePage } from '../../utils/livePageSimpleFixer';
import AIRefreshIcon from '../../components/AIRefreshIcon';
import TipTapInlineEditor from '../WidgetEditComponents/TipTapInlineEditor';
import EditableButton from '../WidgetEditComponents/EditableButton';
import HeroContentElement from '../WidgetEditComponents/HeroContentElement';
import ContainerEditControls from '../WidgetEditComponents/ContainerEditControls';
import ContainerFloatingToolbar from '../WidgetEditComponents/ContainerFloatingToolbar';
import LayoutFloatingToolbar from '../WidgetEditComponents/LayoutFloatingToolbar';
import DynamicContainerSection from '../WidgetEditComponents/DynamicContainerSection';
import AIPromptPopup from '../WidgetEditComponents/AIPromptPopup';
import { getCloudFunctionUrl, authenticatedFetch } from '../../utils/cloudFunctions';
import { 
    migrateToContainerSections,
    addContainer, 
    deleteContainer, 
    updateContainer,
    getGridFlexBasis 
} from './HeroHeaderContainerHelpers';
import { useDebouncedCallback } from '../../hooks/useDebounce';

function appendPercent(value) {
    if (typeof value === 'string' && isNaN(value)) {
        return value;
    }
    const percentValue = Math.min(Number(value), 100);
    return `${percentValue}%`;
}

function HeroHeader({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget, onDuplicate, colors = {}, colorAssignments = {}, spaceFonts = {} }) {
    const defaultHeroImage = '/img/yoga.webp';
    const defaultLogo = '/img/your-logo.webp';
    const deleteLogo = '/img/delete.svg';

  const fonts = useFont();
  
  // ✅ Merge FontContext mit Space Fonts (Space Fonts haben Priorität!)
  const activeFonts = {
    ...fonts,
    globalBigFont: spaceFonts.globalBigFont || fonts.globalBigFont || 'Arial',
    globalSmallFont: spaceFonts.globalSmallFont || fonts.globalSmallFont || 'Arial'
  };
  
  // ✨ Prepare available fonts list for TipTap Font Family Picker
  const availableFonts = useMemo(() => {
    // ✅ Nutze die gleichen Fonts wie im HeroHeaderEdit (aus fontsHelper.js)
    const allSystemFonts = [...titleFonts, ...textFonts];
    
    // Add custom fonts
    const customFontNames = (fonts.customFonts || []).map(font => font.name);
    
    // Combine all fonts (custom first, dann alle system fonts)
    return [...new Set([...customFontNames, ...allSystemFonts])];
  }, [fonts.customFonts]);
  
  // 🚀 NEW ELEMENT-BASED SYSTEM 2025 - Content Elements State
  const contentElements = useMemo(() => {
    if (!data.contentElements || data.contentElements.length === 0) {
      return [];
    }
    // Sort by order and filter visible elements
    return [...data.contentElements]
      .filter(el => el.visible !== false)
      .sort((a, b) => a.order - b.order);
  }, [data.contentElements]);

  // 🎯 GLOBAL EDITING STATE - Nur ein Element kann gleichzeitig editiert werden
  const [currentlyEditingElementId, setCurrentlyEditingElementId] = useState(null);

  // 🎯 GLOBAL TOOLBAR STATE - Nur eine Toolbar kann gleichzeitig geöffnet sein
  // Mögliche Werte: null | 'container-0' | 'container-1' | 'container-2' | ... | 'layout'
  const [currentlyOpenToolbar, setCurrentlyOpenToolbar] = useState(null);

  // 🎨 LAYOUT TOOLBAR STATE - Widget Layout Settings
  const [isLayoutToolbarVisible, setIsLayoutToolbarVisible] = useState(false);
  
  // 🎨 Refs für Container (für Toolbar Positioning) - Dynamic Array für alle Container
  const containerRefsArray = useRef([]);

  // 🎯 SYNCHRONIZE TOOLBAR STATES
  useEffect(() => {
    if (currentlyOpenToolbar === 'layout') {
      setIsLayoutToolbarVisible(true);
      setCurrentlyEditingElementId(null);
    } else {
      setIsLayoutToolbarVisible(false);
    }
  }, [currentlyOpenToolbar]);

  // 🎯 CLOSE ALL TOOLBARS when Element starts editing
  useEffect(() => {
    if (currentlyEditingElementId !== null) {
      setCurrentlyOpenToolbar(null);
    }
  }, [currentlyEditingElementId]);

  // 🚀 DYNAMIC CONTAINER SECTIONS SYSTEM 2025
  // ✅ ONE-TIME MIGRATION: Nur beim ersten Mount für alte Widgets
  // ❌ NO RE-MIGRATION: Bei Updates direkt data verwenden
  const [hasRunMigration, setHasRunMigration] = useState(false);
  const [workingData, setWorkingData] = useState(data);

  // ✅ Run migration ONLY ONCE when component mounts (for old widgets)
  useEffect(() => {
      if (!hasRunMigration) {
          // console.log('🔄 [ONE-TIME MIGRATION] Checking if migration needed...');
          // console.log('🔄 [ONE-TIME MIGRATION] data.containerSections:', data.containerSections);
          // console.log('🔄 [ONE-TIME MIGRATION] data.contentElements:', data.contentElements);
          
          // Wenn keine containerSections aber contentElements existieren = ALTES Widget
          if ((!data.containerSections || data.containerSections.length === 0) && data.contentElements) {
              // console.log('🔄 [ONE-TIME MIGRATION] OLD WIDGET DETECTED - Running migration...');
              const migratedData = migrateToContainerSections(data);
              setWorkingData(migratedData);
              // ✅ CRITICAL: Speichere migrierte Daten sofort in DB!
              if (onUpdate) {
                  // console.log('💾 [ONE-TIME MIGRATION] Saving migrated data to database...');
                  onUpdate(migratedData);
              }
          } else {
              // console.log('✅ [ONE-TIME MIGRATION] NEW WIDGET - No migration needed');
              setWorkingData(data);
          }
          setHasRunMigration(true);
      }
  }, [hasRunMigration, data, onUpdate]);

  // ✅ Update workingData when data changes (but AFTER initial migration)
  useEffect(() => {
      if (hasRunMigration) {
          // console.log('🔄 [DATA UPDATE] Updating workingData with new data:', data);
          setWorkingData(data);
          
          // ✅ WICHTIG: Clear optimistic styles wenn Firebase-Daten ankommen
          // (Die Firebase-Daten enthalten jetzt die gespeicherten Werte)
          setContainerOptimisticStyles({});
          // console.log('✅ [DATA UPDATE] Cleared optimistic styles (Firebase data received)');
      }
  }, [data, hasRunMigration]);

  const containerSections = useMemo(() => {
      // console.log('🔄 [CONTAINER SECTIONS] Using workingData.containerSections:', workingData.containerSections);
      if (!workingData.containerSections || workingData.containerSections.length === 0) {
          // console.log('⚠️ [CONTAINER SECTIONS] No containerSections found, returning empty array');
          return [];
      }
      const filtered = [...workingData.containerSections]
          .filter(c => c.visible !== false)
          .sort((a, b) => a.order - b.order);
      // console.log('✅ [CONTAINER SECTIONS] Filtered and sorted:', filtered.length, 'sections');
      return filtered;
  }, [workingData.containerSections]);

  // ✅ Initialize refs array for all containers (AFTER containerSections is defined)
  useEffect(() => {
    if (containerSections.length > 0) {
      containerRefsArray.current = containerRefsArray.current.slice(0, containerSections.length);
    }
  }, [containerSections.length]);

  // ⚡ OPTIMISTIC UI: Lokaler State für Container-Styles (Map: containerId → styles)
  const [containerOptimisticStyles, setContainerOptimisticStyles] = useState({});

  // 🚀 DEBOUNCED UPDATE - Verhindert dass jeder Slider-Move sofort in DB schreibt
  // 100ms Delay für Firebase Updates (UI-Updates sind instant via local state)
  const debouncedUpdate = useDebouncedCallback((newData) => {
      // console.log('💾 [DEBOUNCED] Saving to Firebase after delay...');
      if (onUpdate) {
          onUpdate(newData);
      }
  }, 100);

  // Container Management Functions
  const handleContainerUpdate = (updatedContainer, isSliderUpdate = false) => {
      // console.log('🔄 [HeroHeader] handleContainerUpdate called:', {
      //     updatedContainerId: updatedContainer.id,
      //     isSliderUpdate,
      //     hasOnUpdate: !!onUpdate,
      //     workingData: workingData
      // });
      
      // ✅ WICHTIG: workingData IMMER sofort updaten (für Add/Delete Container)
      const newData = updateContainer(workingData, updatedContainer);
      setWorkingData(newData); // ⚡ SOFORT updaten!
      
      // ⚡ OPTIMISTIC UI: Bei Slider-Updates ZUSÄTZLICH optimistic state für UI
      if (isSliderUpdate) {
          // console.log('⚡ [OPTIMISTIC] Updating local container styles for:', updatedContainer.id);
          setContainerOptimisticStyles(prev => ({
              ...prev,
              [updatedContainer.id]: updatedContainer
          }));
      } else {
          // ✅ Bei NON-Slider Updates: Clear optimistic state (ist jetzt in workingData)
          setContainerOptimisticStyles(prev => {
              const newStyles = { ...prev };
              delete newStyles[updatedContainer.id];
              return newStyles;
          });
      }
      
      // console.log('✅ [HeroHeader] Calling DEBOUNCED update...');
      
      // ✅ DEBOUNCED: Nur nach 100ms Pause wird wirklich in Firebase gespeichert
      debouncedUpdate(newData);
  };

  const handleContainerDelete = (containerId) => {
      const newData = deleteContainer(workingData, containerId);
      setWorkingData(newData); // ⚡ SOFORT updaten!
      
      // ✅ Clear optimistic styles für gelöschten Container
      setContainerOptimisticStyles(prev => {
          const newStyles = { ...prev };
          delete newStyles[containerId];
          return newStyles;
      });
      
      // ❌ NICHT debounced - Delete soll sofort passieren
      onUpdate(newData);
  };

  const handleAddContainer = (containerType) => {
      // console.log('🚀 [ADD CONTAINER] Called with containerType:', containerType);
      // console.log('🚀 [ADD CONTAINER] workingData:', workingData);
      // console.log('🚀 [ADD CONTAINER] Current containerSections:', workingData.containerSections);
      
      // ✅ WICHTIG: Nutze aktuelles workingData (enthält alle optimistischen Änderungen!)
      const newData = addContainer(workingData, containerType);
      setWorkingData(newData); // ⚡ SOFORT updaten!
      
      // console.log('🚀 [ADD CONTAINER] New data created:', newData);
      // console.log('🚀 [ADD CONTAINER] New containerSections:', newData.containerSections);
      // console.log('🚀 [ADD CONTAINER] Has onUpdate?', !!onUpdate);
      
      // ❌ NICHT debounced - Add soll sofort passieren
      onUpdate(newData);
      
      // console.log('✅ [ADD CONTAINER] onUpdate called');
  };

  // 🚀 Element Management Functions
  const handleElementUpdate = (updatedElement) => {
    if (!onUpdate || !data.contentElements) return;
    
    const newElements = data.contentElements.map(el => 
      el.id === updatedElement.id ? updatedElement : el
    );
    
    // ✅ DEBOUNCED: Element-Updates (z.B. Text-Änderungen, Slider)
    debouncedUpdate({ ...data, contentElements: newElements });
  };

  const handleElementDelete = (elementId) => {
    if (!onUpdate || !data.contentElements) return;
    
    const newElements = data.contentElements.filter(el => el.id !== elementId);
    // ❌ NICHT debounced - Delete soll sofort passieren
    onUpdate({ ...data, contentElements: newElements });
  };

  // 🔄 Element Reorder Functions - Move Up/Down
  const handleElementMoveUp = (elementId) => {
    if (!onUpdate || !data.contentElements) return;
    
    const currentIndex = contentElements.findIndex(el => el.id === elementId);
    if (currentIndex <= 0) return; // Already at top
    
    // Swap order with previous element
    const newElements = [...data.contentElements];
    const currentElement = newElements.find(el => el.id === elementId);
    const previousElement = newElements.find(el => el.id === contentElements[currentIndex - 1].id);
    
    if (currentElement && previousElement) {
      const tempOrder = currentElement.order;
      currentElement.order = previousElement.order;
      previousElement.order = tempOrder;
    }
    
    onUpdate({ ...data, contentElements: newElements });
  };

  const handleElementMoveDown = (elementId) => {
    if (!onUpdate || !data.contentElements) return;
    
    const currentIndex = contentElements.findIndex(el => el.id === elementId);
    if (currentIndex >= contentElements.length - 1) return; // Already at bottom
    
    // Swap order with next element
    const newElements = [...data.contentElements];
    const currentElement = newElements.find(el => el.id === elementId);
    const nextElement = newElements.find(el => el.id === contentElements[currentIndex + 1].id);
    
    if (currentElement && nextElement) {
      const tempOrder = currentElement.order;
      currentElement.order = nextElement.order;
      nextElement.order = tempOrder;
    }
    
    onUpdate({ ...data, contentElements: newElements });
  };

  const handleElementAdd = (type, elementType, position, referenceIndex) => {
    if (!onUpdate) return;
    
    // 🔍 DEBUG: Log colors and colorAssignments
    console.log('🎨 [HERO HEADER] Creating new element:', {
      type,
      elementType,
      colors,
      colorAssignments,
      activeFonts
    });
    
    const currentElements = data.contentElements || [];
    const newOrder = position === 'before' ? referenceIndex : referenceIndex + 1;
    
    // ✅ Default Styling basierend auf elementType (H1, H2, H3, P)
    const getDefaultTextStyling = (elType) => {
      const defaults = {
        'h1': {
          fontSize: 48,
          fontSizeMobile: 36,
          fontWeight: '700',
          fontWeightMobile: '700',
          lineHeight: 1.2,
          lineHeightMobile: 1.2,
          maxWidth: 800,
          maxWidthMobile: 700,
          marginBottom: 0, // ✅ Kein Default Margin
          marginBottomMobile: 0,
          placeholder: 'Enter H1 heading...',
          content: 'New Heading'
        },
        'h2': {
          fontSize: 36,
          fontSizeMobile: 28,
          fontWeight: '600',
          fontWeightMobile: '600',
          lineHeight: 1.3,
          lineHeightMobile: 1.3,
          maxWidth: 700,
          maxWidthMobile: 600,
          marginBottom: 0, // ✅ Kein Default Margin
          marginBottomMobile: 0,
          placeholder: 'Enter H2 heading...',
          content: 'New Subheading'
        },
        'h3': {
          fontSize: 24,
          fontSizeMobile: 20,
          fontWeight: '600',
          fontWeightMobile: '600',
          lineHeight: 1.4,
          lineHeightMobile: 1.4,
          maxWidth: 650,
          maxWidthMobile: 550,
          marginBottom: 0, // ✅ Kein Default Margin
          marginBottomMobile: 0,
          placeholder: 'Enter H3 heading...',
          content: 'New Subtitle'
        },
        'p': {
          fontSize: 16,
          fontSizeMobile: 14,
          fontWeight: '400',
          fontWeightMobile: '400',
          lineHeight: 1.6,
          lineHeightMobile: 1.6,
          maxWidth: 600,
          maxWidthMobile: 500,
          marginBottom: 0,
          marginBottomMobile: 0,
          placeholder: 'Enter paragraph...',
          content: 'New paragraph text...'
        },
        'custom': {
          fontSize: 16,
          fontSizeMobile: 14,
          fontWeight: '400',
          fontWeightMobile: '400',
          lineHeight: 1.6,
          lineHeightMobile: 1.6,
          maxWidth: 600,
          maxWidthMobile: 500,
          marginBottom: 0,
          marginBottomMobile: 0,
          placeholder: 'Enter text...',
          content: 'New text element...'
        }
      };
      
      return defaults[elType] || defaults['custom'];
    };
    
    // Create new element with default values
    const newElement = {
      id: `${type}-${Date.now()}`,
      type: type,
      order: newOrder,
      visible: true,
      elementType: type === 'button' ? 'button' : elementType,
      
      // Default text styling basierend auf elementType
      ...(type === 'text' && {
        ...getDefaultTextStyling(elementType),
        // ✅ FIXED: Nutze Brand Font basierend auf elementType (H1 = Big Font, Rest = Small Font)
        fontFamily: elementType === 'h1' 
          ? (activeFonts.globalBigFont || '') 
          : (activeFonts.globalSmallFont || ''),
        // ✅ FIXED: Nutze Brand Text Color basierend auf colorAssignments
        // H1 = primaryText, H2/H3 = primaryText, P/custom = secondaryText
        color: (() => {
          if (elementType === 'h1' || elementType === 'h2' || elementType === 'h3') {
            const colorKey = colorAssignments.primaryText || 'color2';
            const resolvedColor = colors[colorKey] || '#333333';
            console.log(`🎨 [COLOR ASSIGNMENT] ${elementType.toUpperCase()} - colorKey: ${colorKey}, resolvedColor: ${resolvedColor}`);
            return resolvedColor;
          } else {
            const colorKey = colorAssignments.secondaryText || 'color3';
            const resolvedColor = colors[colorKey] || '#666666';
            console.log(`🎨 [COLOR ASSIGNMENT] ${elementType.toUpperCase()} - colorKey: ${colorKey}, resolvedColor: ${resolvedColor}`);
            return resolvedColor;
          }
        })(),
        letterSpacing: 0,
        letterSpacingMobile: 0,
        textAlign: 'center', // ✅ DEFAULT: Mittig
        textAlignMobile: 'center', // ✅ DEFAULT: Mittig auf Mobile
        marginTop: 0,
        marginRight: 0,
        marginLeft: 0,
        marginTopMobile: 0,
        marginRightMobile: 0,
        marginLeftMobile: 0,
        textEffect: ''
      }),
      
      // Default button styling
      ...(type === 'button' && {
        buttonText: 'Click me',
        buttonLink: '',
        buttonTextSize: 18,
        buttonTextSizeMobile: 16,
        buttonTextWeight: '600',
        buttonTextWeightMobile: '600',
        // ✅ FIXED: Nutze Brand Font (Small Font für Buttons)
        buttonFontFamily: activeFonts.globalSmallFont || '',
        buttonWidth: 200,
        buttonWidthDesktop: 200,
        buttonWidthMobile: 100,
        buttonMaxWidth: 300,
        buttonMaxWidthMobile: 280,
        buttonHeight: 50,
        buttonHeightMobile: 48,
        buttonBorderRadius: 8,
        buttonBorderRadiusMobile: 8,
        // ✅ FIXED: Nutze Brand Button Color basierend auf colorAssignments
        buttonColor: colors[colorAssignments.buttons || 'color1'] || '#5C80BC',
        buttonTextColor: colors[colorAssignments.buttonText || 'color5'] || '#FFFFFF',
        buttonGradientEnabled: false,
        buttonGradientColor1: '#667eea',
        buttonGradientColor2: '#764ba2',
        buttonGradientAnimation: false,
        buttonShadowEnabled: false,
        buttonShadowIntensity: 15,
        buttonShadowDistance: 8,
        buttonShadowOpacity: 25,
        buttonShadowColor: '#000000',
        buttonBorderEnabled: false,
        buttonBorderWidth: 2,
        buttonBorderColor: '#5C80BC',
        buttonArrowEnabled: false,
        buttonArrowDirection: 'left',
        buttonAlignment: 'left', // ✅ NEW: Button Alignment (left, center, right)
        buttonAlignmentMobile: 'left', // ✅ NEW: Mobile Button Alignment
        buttonAnimationsEnabled: false,
        buttonAnimationType: 'gentle-pulse',
        buttonAnimationSpeed: 2000,
        marginTop: 0, // ✅ Kein Default Margin
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
        marginTopMobile: 0, // ✅ Kein Default Margin Mobile
        marginBottomMobile: 0,
        marginLeftMobile: 0,
        marginRightMobile: 0,
        buttonEffect: ''
      }),
      
      // Default divider styling
      ...(type === 'divider' && {
        dividerStyle: 'simple-line',
        dividerColor: colors.accentColor || '#5C80BC',
        dividerThickness: 2,
        dividerWidth: 100,
        dividerWidthType: 'percent', // 'percent' or 'pixel'
        dividerMaxWidth: 800,
        marginTop: 20,
        marginBottom: 20,
        marginLeft: 0,
        marginRight: 0,
        marginTopMobile: 20,
        marginBottomMobile: 20,
        marginLeftMobile: 0,
        marginRightMobile: 0
      }),
      
      // Default image styling
      ...(type === 'image' && {
        imageUrl: '',
        altText: 'Hero Image',
        // ✅ NEW DEFAULTS: 300px width, auto height, no shadow/border
        width: 300, // ✅ 300px width
        widthMobile: 300,
        height: 'auto', // ✅ Auto height
        heightMobile: 'auto',
        maxWidth: 1200,
        maxWidthMobile: 800,
        widthType: 'pixel', // ✅ Pixel for fixed width
        heightType: 'auto', // ✅ Auto height
        borderRadius: 0, // ✅ No border radius
        borderRadiusMobile: 0,
        objectFit: 'cover', // 'cover', 'contain', 'fill', 'none'
        alignment: 'center', // 'left', 'center', 'right'
        // ✅ Border & Shadow disabled by default
        borderEnabled: false, // ✅ No border
        borderWidth: 1,
        borderColor: '#e0e0e0',
        shadowEnabled: false, // ✅ No shadow
        shadowIntensity: 15,
        shadowDistance: 4,
        shadowOpacity: 15,
        // ✅ Padding für Icon-Container Support
        padding: 0,
        paddingMobile: 0,
        containerBgColor: '#ffffff',
        marginTop: 0,
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
        marginTopMobile: 0,
        marginBottomMobile: 0,
        marginLeftMobile: 0,
        marginRightMobile: 0
      }),
      
      // Default Q&A styling
      ...(type === 'qa' && {
        // Container Styling
        qaBackgroundColor: '#ffffff',
        qaBorderEnabled: true,
        qaBorderWidth: 1,
        qaBorderColor: '#e0e0e0',
        qaBorderRadius: 12,
        qaBorderRadiusMobile: 10,
        qaShadowEnabled: true,
        qaShadowIntensity: 20,
        qaShadowDistance: 6,
        qaShadowOpacity: 15,
        qaPadding: 20,
        qaPaddingMobile: 16,
        qaGap: 12,
        qaTextAlign: 'left',
        qaTextAlignMobile: 'left',
        
        // Item Styling
        qaItemBackgroundColor: '#f8f9fa',
        qaItemBorderEnabled: false,
        qaItemBorderWidth: 1,
        qaItemBorderColor: '#e0e0e0',
        qaItemBorderRadius: 8,
        qaItemBorderRadiusMobile: 6,
        qaItemShadowEnabled: false,
        qaItemShadowIntensity: 10,
        qaItemShadowDistance: 2,
        qaItemShadowOpacity: 10,
        qaItemPadding: 16,
        qaItemPaddingMobile: 12,
        qaSeparatorLineMode: false,
        qaSeparatorLineWidth: 1,
        qaSeparatorLineColor: '#e0e0e0',
        
        // Icon Settings
        qaIconStyle: 'chevron', // 'chevron', 'angle', 'angle-double', 'caret-sharp', 'long-arrow', 'arrow-thin', 'plus-minus', 'circle-plus', 'chevron-circle'
        qaIconColor: colors.accentColor || '#5C80BC',
        
        // Q&A Items (3 Demo Q&As)
        qaItems: [
          {
            id: `qa-${Date.now()}-1`,
            question: '<p><strong>What is this service about?</strong></p>',
            answer: '<p>This is a great service that helps you achieve your goals. We provide professional support and guidance throughout your journey.</p>',
            visible: true
          },
          {
            id: `qa-${Date.now()}-2`,
            question: '<p><strong>How does it work?</strong></p>',
            answer: '<p>It\'s simple! Just follow our step-by-step process and you\'ll see results in no time. Our team is here to support you every step of the way.</p>',
            visible: true
          },
          {
            id: `qa-${Date.now()}-3`,
            question: '<p><strong>What are the benefits?</strong></p>',
            answer: '<p>You\'ll gain access to exclusive features, expert guidance, and a supportive community. Plus, you can track your progress and celebrate your achievements!</p>',
            visible: true
          }
        ],
        
        marginTop: 0,
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
        marginTopMobile: 0,
        marginBottomMobile: 0,
        marginLeftMobile: 0,
        marginRightMobile: 0
      }),
      
      // Default Countdown Bar styling
      ...(type === 'countdownBar' && {
        text: 'Limited Time Offer!',
        countdownMode: 'monthly', // 'fixed' or 'monthly'
        targetDate: '', // ISO datetime string for fixed mode
        
        // Bar Styling
        barHeight: 100,
        barHeightMobile: 80,
        barBorderRadius: 12,
        barBorderRadiusMobile: 12,
        barPadding: 20,
        barPaddingMobile: 20,
        barBackgroundColor: colors[colorAssignments.buttons || 'color1'] || '#667eea',
        barGradientEnabled: true,
        barGradientColor1: '#667eea',
        barGradientColor2: '#764ba2',
        barBorderEnabled: false,
        barBorderWidth: 2,
        barBorderColor: '#ffffff',
        barShadowEnabled: true,
        shadowIntensity: 20,
        shadowDistance: 8,
        shadowOpacity: 20,
        
        // Text Styling
        textFontSize: 18,
        textFontSizeMobile: 16,
        textFontWeight: '500',
        textFontWeightMobile: '500',
        textFontFamily: activeFonts.globalSmallFont || '',
        textColor: '#ffffff',
        
        // Countdown Styling
        countdownFontSize: 32,
        countdownFontSizeMobile: 28,
        countdownFontWeight: '700',
        countdownFontWeightMobile: '700',
        countdownFontFamily: activeFonts.globalBigFont || '',
        countdownColor: '#ffffff',
        
        // Timer Container Styling
        timerPosition: 'right', // 'left', 'center', 'right'
        timerLayout: 'horizontal', // 'horizontal' or 'stacked'
        timerGap: 12,
        timerGapMobile: 10,
        timerContainerPadding: 12,
        timerContainerPaddingMobile: 10,
        timerContainerBorderRadius: 8,
        timerContainerBorderRadiusMobile: 8,
        timerContainerBackgroundColor: 'rgba(255, 255, 255, 0.1)',
        timerContainerBorderEnabled: true,
        timerContainerBorderWidth: 1,
        timerContainerBorderColor: 'rgba(255, 255, 255, 0.2)',
        timerContainerShadowEnabled: false,
        timerShadowIntensity: 10,
        timerShadowDistance: 4,
        timerShadowOpacity: 15,
        
        // Display Format
        showDays: true,
        showHours: true,
        showMinutes: true,
        showSeconds: true,
        labelDisplayMode: 'short', // 'short', 'long', 'none', 'custom'
        // Custom Labels (für deutsche Labels etc.)
        customDayLabel: 'Tage',
        customHourLabel: 'Std',
        customMinuteLabel: 'Min',
        customSecondLabel: 'Sek',
        dayLabel: 'd',
        hourLabel: 'h',
        minuteLabel: 'm',
        secondLabel: 's',
        
        // Margins
        marginTop: 0,
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
        marginTopMobile: 0,
        marginBottomMobile: 0,
        marginLeftMobile: 0,
        marginRightMobile: 0
      }),
      
      // Default Footer styling
      ...(type === 'footer' && {
        // Layout & Structure
        footerLayout: 'centered', // 'centered', '3-column', '2-column', 'stacked'
        
        // Content
        logoUrl: '',
        sloganText: 'Your Slogan Here',
        socialLinks: [
          { icon: 'fa-facebook-f', url: 'https://facebook.com' },
          { icon: 'fa-instagram', url: 'https://instagram.com' },
          { icon: 'fa-twitter', url: 'https://twitter.com' }
        ],
        navigationLinks: [
          { label: 'Privacy', url: '#' },
          { label: 'Terms', url: '#' },
          { label: 'Contact', url: '#' }
        ],
        
        // Logo Sizing
        logoWidth: 150,
        logoWidthMobile: 120,
        
        // Slogan Styling
        sloganFontSize: 16,
        sloganFontSizeMobile: 14,
        
        // Link Styling
        linkFontSize: 15,
        linkFontSizeMobile: 14,
        
        // Social Icon Sizing
        socialIconSize: 24,
        socialIconSizeMobile: 20,
        
        // Spacing
        footerPaddingTop: 80,
        footerPaddingBottom: 80,
        footerPaddingLeft: 60,
        footerPaddingRight: 60,
        footerPaddingTopMobile: 60,
        footerPaddingBottomMobile: 60,
        footerPaddingLeftMobile: 40,
        footerPaddingRightMobile: 40,
        footerGap: 30,
        footerGapMobile: 20,
        
        // Colors
        footerBackgroundColor: colors.color1 || '#1a1a1a',
        footerTextColor: '#ffffff',
        footerLinkColor: '#a0a0a0',
        footerLinkHoverColor: '#ffffff',
        
        // Border
        footerBorderTopEnabled: true,
        borderTopWidth: 1,
        footerBorderTopColor: '#333333'
      })
    };
    
    // Update order of existing elements
    const updatedElements = currentElements.map(el => ({
      ...el,
      order: el.order >= newOrder ? el.order + 1 : el.order
    }));
    
    // Add new element
    const newElements = [...updatedElements, newElement];
    
    onUpdate({ ...data, contentElements: newElements });
  };
  
    // Inline Editing States
  const [isInlineEditing, setIsInlineEditing] = useState(null);
  const [inlineValue, setInlineValue] = useState('');
  const [originalLayout, setOriginalLayout] = useState({});
  
  // Image Upload States
  const [isUploadingHero, setIsUploadingHero] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  
    // Animation Trigger State (2025 Animation System)
  const [animationKey, setAnimationKey] = useState(0);
    
    // Mobile Layout State - MUSS FRÜH definiert werden
  const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showWidgetMenu, setShowWidgetMenu] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const containerRef = useRef(null);
    const widgetMenuRef = useRef(null);
  
    // Ref für Outside-Click Detection
  const widgetRef = useRef(null);
  
  // Animation Trigger Effect (2025 Animation System)
  useEffect(() => {
    if (data.triggerAnimation && isEditMode) {
      setAnimationKey(prev => prev + 1);
    }
  }, [data.triggerAnimation, isEditMode]);
  
    // 🚀 NEUE LAYOUT CONFIGURATION - Nutzt LayoutEngine aber behält Original-Struktur
    const getLayoutConfig = () => {
        const layoutType = data.layoutType || 'classic-split';
        
        // Versuche Layout aus LayoutEngine zu holen
        try {
            const engineLayout = LayoutEngine.generateLayout('heroHeader', layoutType, data, 'desktop');
            
            // Konvertiere LayoutEngine Format zu Original Format
            const features = engineLayout.features || {};
            
            return {
                textFlex: engineLayout.sections?.text?.flex || '1',
                imageFlex: engineLayout.sections?.image?.flex || '1', 
                direction: engineLayout.container?.flexDirection === 'row-reverse' ? 'row' : 
                          (engineLayout.container?.flexDirection || 'row'),
                textAlign: engineLayout.sections?.text?.alignment || 'left',
                imagePosition: engineLayout.sections?.image?.position || 'right',
                overlayMode: features.overlayMode || false,
                mobileStack: features.mobileStack !== false,
                hideSubtitle: features.hideSubtitle || false,
                hideAdditional: features.hideAdditional || false,
                descriptionAfterTitle: features.descriptionAfterTitle || false,
                smallerText: features.compactText || false,
                roundButton: features.roundButton || false,
                defaultImageWidth: features.defaultImageWidth || 50,
            };
        } catch (error) {
            console.warn('LayoutEngine fallback for', layoutType);
            
            // Fallback zu Original-Konfiguration
            const layouts = {
                'classic-split': {
                    textFlex: '1',
                    imageFlex: '1',
                    direction: 'row',
                    textAlign: 'left',
                    imagePosition: 'right',
                    overlayMode: false,
                    mobileStack: true,
                    defaultImageWidth: 50
                },
                'reverse-split': {
                    textFlex: '1',
                    imageFlex: '1',
                    direction: 'row',
                    textAlign: 'left',
                    imagePosition: 'left',
                    overlayMode: false,
                    mobileStack: true,
                    defaultImageWidth: 50
                },
                'text-dominant': {
                    textFlex: '2.3',
                    imageFlex: '1',
                    direction: 'row',
                    textAlign: 'left',
                    imagePosition: 'right',
                    overlayMode: false,
                    mobileStack: true
                },
                'full-width-overlay': {
                    textFlex: '1',
                    imageFlex: '0',
                    direction: 'column',
                    textAlign: 'center',
                    imagePosition: 'background',
                    overlayMode: true,
                    mobileStack: false
                },
                'centered-content': {
                    textFlex: '1',
                    imageFlex: '1',
                    direction: 'column',
                    textAlign: 'center',
                    imagePosition: 'top',
                    overlayMode: false,
                    mobileStack: false
                },
                'classic-minimal': {
                    textFlex: '1',
                    imageFlex: '1',
                    direction: 'row',
                    textAlign: 'left',
                    imagePosition: 'right',
                    overlayMode: false,
                    mobileStack: true,
                    hideSubtitle: true,
                    hideAdditional: true,
                    descriptionAfterTitle: true
                },
                'minimalistic': {
                    textFlex: '1',
                    imageFlex: '1',
                    direction: 'row',
                    textAlign: 'left',
                    imagePosition: 'right',
                    overlayMode: false,
                    mobileStack: true,
                    smallerText: true,
                    roundButton: true,
                    defaultImageWidth: 60
                }
            };
            
            return layouts[layoutType] || layouts['classic-split'];
        }
    };

    const layoutConfig = getLayoutConfig();

    // Text Alignment Configuration - RESPONSIVE 2025
    const getTextAlignmentStyles = () => {
        // ✅ RESPONSIVE: Separate Desktop/Mobile Alignment
        let alignment;
        
        if (isMobileLayout) {
            // Mobile: textAlignmentMobile > textAlignment > Layout default > center
            alignment = data.textAlignmentMobile || data.textAlignment || layoutConfig.textAlign || 'center';
        } else {
            // Desktop: textAlignment > Layout default > left
            alignment = data.textAlignment || layoutConfig.textAlign || 'left';
        }
        
        console.log(`🎯 TextAlignment: ${isMobileLayout ? 'Mobile' : 'Desktop'} = ${alignment}`);
        
        const alignmentMap = {
            'left': {
                alignItems: 'flex-start',
                textAlign: 'left',
                justifyContent: 'flex-start'
            },
            'center': {
                alignItems: 'center', 
                textAlign: 'center',
                justifyContent: 'center'
            },
            'right': {
                alignItems: 'flex-end',
                textAlign: 'right',
                justifyContent: 'flex-end'
            }
        };
        
        return alignmentMap[alignment] || alignmentMap['left'];
    };

    // Title Separator Styles Configuration - Premium & Elegant
    const getTitleSeparatorStyles = (data) => {
        const style = data.titleSeparatorStyle || 'elegant-line';
        const color = data.titleSeparatorColor || '#5C80BC';
        const thickness = data.titleSeparatorThickness || 2;
        
        const separatorStyles = {
            'elegant-dot': {
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${color} 0%, ${color}cc 60%, ${color}80 80%, transparent 100%)`,
                border: `2px solid ${color}40`,
                boxShadow: `0 3px 12px ${color}60, inset 0 1px 3px ${color}80`
            },
            'elegant-line': {
                height: `${thickness}px`,
                background: `linear-gradient(90deg, transparent 0%, ${color} 50%, transparent 100%)`,
                border: 'none',
                borderRadius: `${thickness / 2}px`
            },
            'premium-thick': {
                height: `${Math.max(thickness, 3)}px`,
                background: `linear-gradient(135deg, ${color} 0%, ${color}cc 50%, ${color} 100%)`,
                border: 'none',
                borderRadius: `${thickness / 2}px`,
                boxShadow: `0 2px 6px ${color}30`
            },
            'luxury-double': {
                height: `${thickness}px`,
                backgroundColor: color,
                border: 'none',
                position: 'relative',
                borderRadius: `${thickness / 2}px`
            },
            'artistic-dashed': {
                height: `${thickness}px`,
                backgroundColor: 'transparent',
                border: 'none',
                borderTop: `${thickness}px dashed ${color}`,
                borderRadius: `${thickness / 2}px`
            },
            'sophisticated-gradient': {
                height: `${thickness}px`,
                background: `linear-gradient(90deg, 
                    ${color}00 0%, 
                    ${color}40 20%, 
                    ${color} 50%, 
                    ${color}40 80%, 
                    ${color}00 100%)`,
                border: 'none',
                borderRadius: `${thickness / 2}px`,
                position: 'relative'
            },
            'diamond-accent': {
                width: '20px',
                height: '20px',
                background: `linear-gradient(135deg, ${color} 0%, ${color}cc 50%, ${color} 100%)`,
                border: `2px solid ${color}60`,
                transform: 'rotate(45deg)',
                borderRadius: '3px',
                boxShadow: `0 3px 12px ${color}50, inset 0 1px 3px ${color}80`
            },
            'wave-elegant': {
                height: `${thickness}px`,
                background: `repeating-linear-gradient(90deg, 
                    ${color} 0px, 
                    ${color} 8px, 
                    transparent 8px, 
                    transparent 12px)`,
                border: 'none',
                borderRadius: `${thickness / 2}px`,
                opacity: 0.8
            }
        };
        
        return separatorStyles[style] || separatorStyles['elegant-line'];
    };

    // Hero Image Side Border Configuration
    const getHeroImageSideBorderStyles = (data) => {
        if (!data.heroImageSideBorder) return {};
        
        const borderWidth = data.heroImageBorderWidth || 3;
        const borderColor = data.heroImageBorderColor || '#5C80BC';
        
        // ✅ DESKTOP: Links/Rechts basierend auf desktopFlipSides
        if (!isMobileLayout) {
            const isFlipped = data.desktopFlipSides;
            return isFlipped ? 
                { borderRight: `${borderWidth}px solid ${borderColor}` } : // Geflippt = Border rechts
                { borderLeft: `${borderWidth}px solid ${borderColor}` };   // Normal = Border links
        }
        
        // ✅ MOBILE: Oben/Unten basierend auf mobileFlipSides
        const isMobileFlipped = data.mobileFlipSides;
        return isMobileFlipped ?
            { borderTop: `${borderWidth}px solid ${borderColor}` } :    // Mobile geflippt = Border oben
            { borderBottom: `${borderWidth}px solid ${borderColor}` };  // Mobile normal = Border unten
    };

    // Logo Position Configuration - ORIGINAL BEIBEHALTEN
    // Hero Image Decorative Mode Position Configuration - Responsive % based
    const getHeroDecorativePositionStyles = (data) => {
        const position = data.heroImageDecorativePosition || 'bottom-right';
        const marginPercent = (data.heroImageDecorativeMargin || 40) / 10; // Convert px to % (40px = 4%)
        const sizePercent = (data.heroImageDecorativeSize || 300) / 10; // Convert px to % (300px = 30%)

        // ✅ MOBILE OVERRIDE: Immer zentriert mit 0 Abstand auf Mobile
        if (isMobileLayout) {
            return {
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                right: 'auto',
                bottom: 'auto',
                width: `${sizePercent}%`,
                height: `${sizePercent}%`
            };
        }

        // Desktop Positions - normale Logik
        const positions = {
            'top-left': {
                top: `${marginPercent}%`,
                left: `${marginPercent}%`,
                right: 'auto',
                bottom: 'auto'
            },
            'top-center': {
                top: `${marginPercent}%`,
                left: '50%',
                transform: 'translateX(-50%)',
                right: 'auto',
                bottom: 'auto'
            },
            'top-right': {
                top: `${marginPercent}%`,
                right: `${marginPercent}%`,
                left: 'auto',
                bottom: 'auto'
            },
            'center-left': {
                top: '50%',
                left: `${marginPercent}%`,
                transform: 'translateY(-50%)',
                right: 'auto',
                bottom: 'auto'
            },
            'center': {
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                right: 'auto',
                bottom: 'auto'
            },
            'center-right': {
                top: '50%',
                right: `${marginPercent}%`,
                transform: 'translateY(-50%)',
                left: 'auto',
                bottom: 'auto'
            },
            'bottom-left': {
                bottom: `${marginPercent}%`,
                left: `${marginPercent}%`,
                right: 'auto',
                top: 'auto'
            },
            'bottom-center': {
                bottom: `${marginPercent}%`,
                left: '50%',
                transform: 'translateX(-50%)',
                right: 'auto',
                top: 'auto'
            },
            'bottom-right': {
                bottom: `${marginPercent}%`,
                right: `${marginPercent}%`,
                left: 'auto',
                top: 'auto'
            }
        };

        return {
            ...positions[position] || positions['bottom-right'],
            width: `${sizePercent}%`,
            height: `${sizePercent}%`
        };
    };

    const getLogoPositionStyles = (data) => {
        // ✅ RESPONSIVE LOGO POSITION - Desktop/Mobile
        const position = isMobileLayout ? 
            (data.logoPositionMobile || data.logoPosition || 'top-center') : 
            (data.logoPosition || 'top-right');
        const margin = data.logoMargin || 20;
        const cornerDistance = data.logoCornerDistance || 20;

        const positions = {
            'top-left': {
                top: `${cornerDistance}px`,
                left: `${margin}px`,
                right: 'auto',
                bottom: 'auto'
            },
            'top-center': {
                top: `${cornerDistance}px`,
                left: '50%',
                transform: 'translateX(-50%)',
                right: 'auto',
                bottom: 'auto'
            },
            'top-right': {
                top: `${cornerDistance}px`,
                right: `${margin}px`,
                left: 'auto',
                bottom: 'auto'
            },
            'center-left': {
                top: '50%',
                left: `${margin}px`,
                transform: 'translateY(-50%)',
                right: 'auto',
                bottom: 'auto'
            },
            'center': {
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                right: 'auto',
                bottom: 'auto'
            },
            'center-right': {
                top: '50%',
                right: `${margin}px`,
                transform: 'translateY(-50%)',
                left: 'auto',
                bottom: 'auto'
            },
            'bottom-left': {
                bottom: `${cornerDistance}px`,
                left: `${margin}px`,
                right: 'auto',
                top: 'auto'
            },
            'bottom-center': {
                bottom: `${cornerDistance}px`,
                left: '50%',
                transform: 'translateX(-50%)',
                right: 'auto',
                top: 'auto'
            },
            'bottom-right': {
                bottom: `${cornerDistance}px`,
                right: `${margin}px`,
                left: 'auto',
                top: 'auto'
            }
        };

        return positions[position] || positions['top-right'];
    };
  
  function isDark(color) {
    // ✅ Safety check: Return false if color is undefined or invalid
    if (!color || typeof color !== 'string' || !color.startsWith('#')) {
      return false;
    }
    const rgb = parseInt(color.slice(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >>  8) & 0xff;
    const b = (rgb >>  0) & 0xff;
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    return luma < 128;
  }
  
  // ✅ NEW: Get outline style for selected state (doesn't interfere with individual borders)
  function getOutlineStyle() {
    if (isEditMode && isSelected) {
      const bgColor = data.backgroundColor || '#ffffff';
      const outlineColor = isDark(bgColor) ? '#FFFFFF' : '#000000';
      return `2px dashed ${outlineColor}`;
    }
    return 'none';
  }
  
  // ✅ DEPRECATED: Old border function kept for backwards compatibility
  function getBorderStyle() {
    // Only used if old data.border property exists
    if (!isEditMode && data.border) {
      return `1.5px solid ${data.borderColor}`;
    }
    return 'none';
  }
  
    // Generate responsive widget styles using new layout system
    const baseWidgetStyle = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor,
        border: getBorderStyle(), // ✅ Only for backwards compatibility
        outline: getOutlineStyle(), // ✅ NEW: Outline for selected state
        outlineOffset: '-2px', // ✅ Outline inside the widget
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%'
    });

    const widgetStyle = {
        ...baseWidgetStyle,
        // ✅ EINHEITLICHE RESPONSIVE HEIGHT LOGIK
        ...(baseWidgetStyle.height === 'auto' && !baseWidgetStyle.minHeight && {
            minHeight: appendPx(isMobileLayout ? 
                (data.heightMobile || 1100) : // Mobile: Template-Default 1100px
                (data.height || 650)         // Desktop: Template-Default 650px
            )
        })
    };

    // ✅ SIMPLIFIED: Container-basierte Breakpoint-Erkennung (wie alle anderen Widgets)
    useEffect(() => {
        // 🎯 PREVIEW MODE FIX: Wenn forceDesktopLayout gesetzt ist, IMMER Desktop-Layout verwenden
        if (data.forceDesktopLayout || data.isPreviewMode || data.isDesktopForced) {
            setIsMobileLayout(false);
            return; // Skip all measurement logic
        }
        
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750); // Standard 750px Breakpoint
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // Smart Outside-Click Handler - ORIGINAL BEIBEHALTEN
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isInlineEditing) return;
            
            const isEditingInput = event.target.tagName === 'TEXTAREA' || event.target.tagName === 'INPUT';
            const isActionButton = event.target.hasAttribute('data-action-button');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit');
            const isAddWidgets = event.target.closest('.add-widgets');
            const isPagePreview = event.target.closest('.page_preview');
            
            const isRealOutsideClick = !isEditingInput && 
                                     !isActionButton && 
                                     !isDeleteButton && 
                                     !isWidgetEdit &&
                                     (isPagePreview || !widgetRef.current?.contains(event.target));
            
            if (isRealOutsideClick) {
                if (onUpdate && inlineValue !== undefined) {
                    const updatedData = { ...data, [isInlineEditing]: inlineValue };
          onUpdate(updatedData);
                }
                setIsInlineEditing(null);
                setInlineValue('');
            }
        };

        if (isInlineEditing) {
            document.addEventListener('mousedown', handleClickOutside, true);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside, true);
            };
        }
    }, [isInlineEditing, inlineValue, data, onUpdate]);

    // 🎯 Widget Menu Outside Click Handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!showWidgetMenu) return;
            
            const clickedInsideMenu = widgetMenuRef.current && widgetMenuRef.current.contains(event.target);
            const clickedMenuButton = event.target.closest('.widget-menu-button');
            
            if (!clickedInsideMenu && !clickedMenuButton) {
                setShowWidgetMenu(false);
            }
        };

        if (showWidgetMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showWidgetMenu]);

    // 🎯 Text Element Outside Click Handler - Deaktiviert Text bei Klick außerhalb
    useEffect(() => {
        // Nur aktiv wenn ein Element gerade editiert wird UND wir im Edit Mode sind
        if (!isEditMode || currentlyEditingElementId === null) return;

        const handleTextElementOutsideClick = (event) => {
            // Prüfe ob Klick innerhalb des HeroHeader Widgets ist
            const clickedInsideWidget = widgetRef.current && widgetRef.current.contains(event.target);
            
            if (!clickedInsideWidget) {
                // Klick ist komplett außerhalb des Widgets → Deaktiviere Element
                console.log('🔒 [Outside Click] Clicked outside widget, deactivating element:', currentlyEditingElementId);
                setCurrentlyEditingElementId(null);
                return;
            }
            
            // 🔧 WICHTIG: Prüfe ob Klick innerhalb IRGENDEINER Floating Toolbar ist
            // Nutze mehrere Methoden für robuste Detection
            const target = event.target;
            
            // Method 1: Check via .closest() für Portal-Container
            const clickedInToolbarPortal = target.closest('.tiptap-bubble-menu-portal') || 
                                          target.closest('.button-floating-toolbar-portal') ||
                                          target.closest('.image-floating-toolbar-portal') ||
                                          target.closest('.container-floating-toolbar-portal') ||
                                          target.closest('.divider-floating-toolbar-portal') ||
                                          target.closest('.layout-floating-toolbar-portal');
            
            // Method 2: Check via class name (für Kinder-Elemente die closest() nicht finden)
            const targetClassName = target.className || '';
            const clickedInToolbarElement = typeof targetClassName === 'string' && (
                targetClassName.includes('tiptap-') ||
                targetClassName.includes('button-toolbar') ||
                targetClassName.includes('button-brand-color') ||
                targetClassName.includes('button-color') ||
                targetClassName.includes('image-toolbar') ||
                targetClassName.includes('container-toolbar') ||
                targetClassName.includes('divider-toolbar') ||
                targetClassName.includes('layout-toolbar') ||
                targetClassName.includes('floating-typo') ||
                targetClassName.includes('typo-control')
            );
            
            if (clickedInToolbarPortal || clickedInToolbarElement) {
                // Klick ist in einer Toolbar → Ignorieren (User editiert)
                console.log('✅ [Outside Click] Clicked in toolbar, keeping element active');
                return;
            }
            
            // Prüfe ob Klick innerhalb des aktuell editierten Elements ist
            const clickedInCurrentElement = target.closest('.universal-text-container') ||
                                           target.closest('.tiptap-inline-wrapper') ||
                                           target.closest('.universal-image-container') ||
                                           target.closest('.universal-button-container') ||
                                           target.closest('.editable-button-wrapper');
            
            if (clickedInCurrentElement) {
                // Klick ist im aktuell editierten Element → Ignorieren (User editiert weiter)
                console.log('✅ [Outside Click] Clicked in current element, keeping active');
                return;
            }
            
            // Prüfe ob Klick auf Container Controls ist (Add/Delete/Reorder Buttons)
            const clickedOnContainerControls = target.closest('.container-edit-controls') ||
                                              target.closest('.hero-content-add-buttons') ||
                                              target.closest('.container-floating-toolbar-portal');
            
            if (clickedOnContainerControls) {
                // Klick ist auf Container-Controls → Deaktiviere Element (User will Container editieren)
                console.log('🎯 [Outside Click] Clicked on container controls, deactivating element');
                setCurrentlyEditingElementId(null);
                return;
            }
            
            // Klick ist irgendwo anders im Widget (z.B. leerer Bereich, anderes Element)
            // → Deaktiviere aktuelles Element
            console.log('🔒 [Outside Click] Clicked elsewhere in widget, deactivating element:', currentlyEditingElementId);
            setCurrentlyEditingElementId(null);
        };

        // Event Listener OHNE Capture-Phase - damit Toolbar-Events zuerst laufen können
        // Das verhindert dass die Toolbar geschlossen wird bevor ihre eigenen Click-Handler laufen
        document.addEventListener('mousedown', handleTextElementOutsideClick);
        
        return () => {
            document.removeEventListener('mousedown', handleTextElementOutsideClick);
        };
    }, [isEditMode, currentlyEditingElementId]);

    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    // 🤖 Handle AI Text Generation - Using AIRefreshIcon logic
    const [isAIRefreshing, setIsAIRefreshing] = useState(false);
    const [showAIPromptPopup, setShowAIPromptPopup] = useState(false);

    const handleAIRefresh = async (params = {}) => {
        if (isAIRefreshing) return;
        
        const userPrompt = params.prompt || '';
        const mode = params.mode || 'improve';
        
        setIsAIRefreshing(true);
        setShowWidgetMenu(false); // Close menu while refreshing
        setShowAIPromptPopup(false); // Close popup
        
        try {
            console.log('🤖 [AI-REFRESH] Starting content refresh for: HeroHeader');
            console.log('📝 [AI-REFRESH] User prompt:', userPrompt || '(none)');
            console.log('🎯 [AI-REFRESH] Mode:', mode);
            
            // Extract current user and space from URL
            const currentPath = window.location.pathname;
            const pathParts = currentPath.split('/').filter(part => part);
            
            let currentUserId = null;
            let currentSpaceName = null;
            
            // Parse URL: /settings/page/userId/spaceName/pageId
            if (pathParts[0] === 'settings' && pathParts[1] === 'page' && pathParts.length >= 4) {
                currentUserId = pathParts[2];
                currentSpaceName = pathParts[3];
            }
            
            if (!currentUserId || !currentSpaceName) {
                throw new Error(`Missing URL context: userId=${currentUserId}, spaceName=${currentSpaceName}`);
            }
            
            // ✅ Extract contentElements from containerSections
            console.log('🔍 [AI-REFRESH] Full widget data structure:', {
                hasData: !!workingData,
                hasContentElements: !!workingData.contentElements,
                hasContainerSections: !!workingData?.containerSections,
                containerCount: workingData?.containerSections?.length
            });
            
            // ✅ NEW: contentElements sind in containerSections[0].contentElements!
            let sourceElements = [];
            if (workingData?.containerSections && workingData.containerSections.length > 0) {
                // Sammle alle contentElements aus allen Containern
                sourceElements = workingData.containerSections.flatMap(container => 
                    container.contentElements || []
                );
                console.log('📦 [AI-REFRESH] Extracted from containerSections');
            } else if (data.contentElements) {
                sourceElements = data.contentElements;
                console.log('📦 [AI-REFRESH] Using direct contentElements');
            }
            
            const textElements = sourceElements.filter(el => el.type === 'text');
            
            console.log('📊 [AI-REFRESH] Source elements:', sourceElements.length);
            console.log('📊 [AI-REFRESH] Found text elements:', textElements.length);
            console.log('📊 [AI-REFRESH] Text elements details:', textElements.map(el => ({
                id: el.id,
                type: el.type,
                elementType: el.elementType,
                hasContent: !!el.content,
                contentPreview: el.content ? el.content.substring(0, 50) : '(empty)'
            })));
            
            // ✅ Get environment-aware Cloud Function URL
            const functionUrl = getCloudFunctionUrl('refreshWidgetContent');
            console.log('🌐 [AI-REFRESH] Using function URL:', functionUrl);
            
            // ✅ Prepare elements for AI: Nur das was die AI braucht (nicht Styling!)
            // AI braucht: id (zum Matchen), content (aktueller Text), elementType (für Context)
            const mappedElements = textElements.map(el => ({
                id: el.id,
                type: el.type,
                elementType: el.elementType, // h1, h2, h3, p - wichtig für Context
                content: el.content,
                order: el.order
            }));
            
            console.log('📊📊📊 [AI-REFRESH] ORIGINAL ELEMENTS WITH STYLES (kept in frontend):');
            textElements.forEach((el, index) => {
                console.log(`\n🎯 Element ${index + 1} [${el.elementType?.toUpperCase()}]:`, {
                    id: el.id,
                    type: el.type,
                    elementType: el.elementType,
                    // FONT STYLES
                    fontSize: el.fontSize,
                    fontSizeMobile: el.fontSizeMobile,
                    fontWeight: el.fontWeight,
                    fontWeightMobile: el.fontWeightMobile,
                    fontFamily: el.fontFamily,
                    color: el.color,
                    // TYPOGRAPHY
                    lineHeight: el.lineHeight,
                    lineHeightMobile: el.lineHeightMobile,
                    letterSpacing: el.letterSpacing,
                    textAlign: el.textAlign,
                    // DIMENSIONS
                    maxWidth: el.maxWidth,
                    maxWidthMobile: el.maxWidthMobile,
                    // MARGINS
                    marginTop: el.marginTop,
                    marginBottom: el.marginBottom,
                    marginLeft: el.marginLeft,
                    marginRight: el.marginRight,
                    // CONTENT
                    contentPreview: el.content?.substring(0, 60) || '',
                    allKeys: Object.keys(el)
                });
            });
            
            console.log('🔍 [AI-REFRESH] Mapped elements for sending:', mappedElements);
            
            // Prepare request body
            const requestBody = {
                widgetData: data,
                spaceId: currentSpaceName,
                widgetType: 'HeroHeader',
                userPrompt: userPrompt, // ✅ User's additional instructions
                mode: mode, // ✅ 'improve' or 'replace'
                currentContent: {
                    // ✅ NEW: Send contentElements structure
                    contentElements: mappedElements
                }
            };
            
            console.log('📤 [AI-REFRESH] Sending request:', {
                url: `${functionUrl}?uid=${currentUserId}`,
                method: 'POST',
                bodyKeys: Object.keys(requestBody),
                textElementCount: textElements.length,
                mappedElementsCount: mappedElements.length,
                hasUserPrompt: !!userPrompt,
                contentElementsInBody: requestBody.currentContent.contentElements.length
            });
            
            // Call Cloud Function for widget content refresh
            const response = await authenticatedFetch(`${functionUrl}?uid=${currentUserId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            console.log('📥 [AI-REFRESH] Response status:', response.status);
            
            if (!response.ok) {
                // Try to get error details from response
                let errorDetails = `HTTP error! status: ${response.status}`;
                try {
                    const errorData = await response.json();
                    console.error('❌ [AI-REFRESH] Error response:', errorData);
                    errorDetails = errorData.error || errorDetails;
                } catch (e) {
                    // Response is not JSON
                    const errorText = await response.text();
                    console.error('❌ [AI-REFRESH] Error text:', errorText);
                    errorDetails = errorText || errorDetails;
                }
                throw new Error(errorDetails);
            }

            const result = await response.json();
            
            console.log('📥 [AI-REFRESH] Full response:', result);
            
            if (result.success && result.refreshedContent) {
                console.log('✅ [AI-REFRESH] Content refreshed successfully');
                console.log('📊 [AI-REFRESH] Refreshed content structure:', {
                    hasContentElements: !!result.refreshedContent.contentElements,
                    contentElementsLength: result.refreshedContent.contentElements?.length,
                    contentElements: result.refreshedContent.contentElements
                });
                
                // ✅ Update contentElements with AI-generated content
                if (result.refreshedContent.contentElements && Array.isArray(result.refreshedContent.contentElements)) {
                    console.log('🔍 [AI-REFRESH] Source elements (from containerSections):', sourceElements);
                    console.log('🔍 [AI-REFRESH] Received contentElements from AI:', result.refreshedContent.contentElements);
                    
                    let updatedContentElements;
                    
                    // ✅ CASE 1: Widget hat KEINE sourceElements → ERSTELLE neue (sollte nicht passieren)
                    if (!sourceElements || sourceElements.length === 0) {
                        console.log('🆕 [AI-REFRESH] No existing elements, CREATING new contentElements from AI');
                        
                        // Konvertiere AI-generierte Elemente in vollständige contentElement-Objekte
                        updatedContentElements = result.refreshedContent.contentElements.map((aiEl, index) => ({
                            id: aiEl.id,
                            type: 'text',
                            elementType: aiEl.id === 'headline' ? 'h1' :
                                        aiEl.id === 'subheadline' ? 'h2' :
                                        aiEl.id === 'description' ? 'h3' :
                                        aiEl.id === 'call-to-action' ? 'p' : 'p',
                            content: aiEl.content,
                            order: index,
                            visible: true,
                            // Default Styling
                            fontSize: aiEl.id === 'headline' ? 48 :
                                     aiEl.id === 'subheadline' ? 36 :
                                     aiEl.id === 'description' ? 20 : 16,
                            fontSizeMobile: aiEl.id === 'headline' ? 36 :
                                           aiEl.id === 'subheadline' ? 28 :
                                           aiEl.id === 'description' ? 18 : 14,
                            fontWeight: aiEl.id === 'headline' || aiEl.id === 'subheadline' ? '700' : '400',
                            fontWeightMobile: aiEl.id === 'headline' || aiEl.id === 'subheadline' ? '700' : '400',
                            lineHeight: 1.5,
                            lineHeightMobile: 1.5,
                            maxWidth: 800,
                            maxWidthMobile: 600,
                            color: '#333333',
                            textAlign: 'left',
                            textAlignMobile: 'left',
                            letterSpacing: 0,
                            letterSpacingMobile: 0,
                            marginTop: 0,
                            marginRight: 0,
                            marginBottom: 0,
                            marginLeft: 0,
                            marginTopMobile: 0,
                            marginRightMobile: 0,
                            marginBottomMobile: 0,
                            marginLeftMobile: 0
                        }));
                        
                        console.log('✅ [AI-REFRESH] Created new elements:', updatedContentElements);
                    } 
                    // ✅ CASE 2: Widget hat contentElements → UPDATE bestehende
                    else {
                        console.log('🔄 [AI-REFRESH] Existing elements found, UPDATING content');
                        
                        // Map durch aktuelle Elemente und versuche Match zu finden
                        updatedContentElements = sourceElements.map(el => {
                            // Find matching refreshed element by ID
                            const refreshedEl = result.refreshedContent.contentElements.find(r => r.id === el.id);
                            
                            console.log('🔍 [AI-REFRESH] Checking element:', {
                                currentId: el.id,
                                currentType: el.type,
                                foundMatch: !!refreshedEl,
                                refreshedId: refreshedEl?.id,
                                hasNewContent: !!refreshedEl?.content
                            });
                            
                            if (refreshedEl && el.type === 'text') {
                                console.log('✅ [AI-REFRESH] MATCH FOUND! Updating element:', el.id);
                                console.log('   Old content:', el.content?.substring(0, 50));
                                console.log('   New content:', refreshedEl.content?.substring(0, 50));
                                
                                console.log('📊 [FRONTEND] ORIGINAL ELEMENT BEFORE UPDATE:', {
                                    id: el.id,
                                    type: el.type,
                                    elementType: el.elementType,
                                    // Font Styles
                                    fontSize: el.fontSize,
                                    fontSizeMobile: el.fontSizeMobile,
                                    fontWeight: el.fontWeight,
                                    fontWeightMobile: el.fontWeightMobile,
                                    fontFamily: el.fontFamily,
                                    color: el.color,
                                    // Typography
                                    lineHeight: el.lineHeight,
                                    lineHeightMobile: el.lineHeightMobile,
                                    letterSpacing: el.letterSpacing,
                                    letterSpacingMobile: el.letterSpacingMobile,
                                    textAlign: el.textAlign,
                                    textAlignMobile: el.textAlignMobile,
                                    // Dimensions
                                    maxWidth: el.maxWidth,
                                    maxWidthMobile: el.maxWidthMobile,
                                    // Margins
                                    marginTop: el.marginTop,
                                    marginRight: el.marginRight,
                                    marginBottom: el.marginBottom,
                                    marginLeft: el.marginLeft,
                                    marginTopMobile: el.marginTopMobile,
                                    marginRightMobile: el.marginRightMobile,
                                    marginBottomMobile: el.marginBottomMobile,
                                    marginLeftMobile: el.marginLeftMobile,
                                    // Other
                                    textEffect: el.textEffect,
                                    order: el.order,
                                    visible: el.visible,
                                    allKeys: Object.keys(el)
                                });
                                
                                console.log('📥 [FRONTEND] RECEIVED FROM BACKEND:', {
                                    id: refreshedEl.id,
                                    // Font Styles
                                    fontSize: refreshedEl.fontSize,
                                    fontSizeMobile: refreshedEl.fontSizeMobile,
                                    fontWeight: refreshedEl.fontWeight,
                                    fontWeightMobile: refreshedEl.fontWeightMobile,
                                    fontFamily: refreshedEl.fontFamily,
                                    color: refreshedEl.color,
                                    // Typography
                                    lineHeight: refreshedEl.lineHeight,
                                    letterSpacing: refreshedEl.letterSpacing,
                                    textAlign: refreshedEl.textAlign,
                                    // Dimensions
                                    maxWidth: refreshedEl.maxWidth,
                                    maxWidthMobile: refreshedEl.maxWidthMobile,
                                    // Margins
                                    marginTop: refreshedEl.marginTop,
                                    marginBottom: refreshedEl.marginBottom,
                                    // Content
                                    contentPreview: refreshedEl.content?.substring(0, 80) || '',
                                    allKeys: Object.keys(refreshedEl)
                                });
                                
                                // ✅ WICHTIG: Behalte ALLE Styling-Properties, ändere NUR den content!
                                const updatedElement = {
                                    ...el, // ✅ Alle existierenden Properties behalten (fontSize, fontWeight, maxWidth, etc.)
                                    content: refreshedEl.content // ✅ NUR Content updaten
                                };
                                
                                console.log(`📊📊📊 [FRONTEND] UPDATED ELEMENT AFTER MERGE [${el.elementType?.toUpperCase()}]:`, {
                                    id: updatedElement.id,
                                    elementType: updatedElement.elementType,
                                    // Font Styles
                                    fontSize: updatedElement.fontSize,
                                    fontSizeMobile: updatedElement.fontSizeMobile,
                                    fontWeight: updatedElement.fontWeight,
                                    fontWeightMobile: updatedElement.fontWeightMobile,
                                    fontFamily: updatedElement.fontFamily,
                                    color: updatedElement.color,
                                    // Typography
                                    lineHeight: updatedElement.lineHeight,
                                    lineHeightMobile: updatedElement.lineHeightMobile,
                                    letterSpacing: updatedElement.letterSpacing,
                                    letterSpacingMobile: updatedElement.letterSpacingMobile,
                                    textAlign: updatedElement.textAlign,
                                    textAlignMobile: updatedElement.textAlignMobile,
                                    // Dimensions
                                    maxWidth: updatedElement.maxWidth,
                                    maxWidthMobile: updatedElement.maxWidthMobile,
                                    // Margins
                                    marginTop: updatedElement.marginTop,
                                    marginRight: updatedElement.marginRight,
                                    marginBottom: updatedElement.marginBottom,
                                    marginLeft: updatedElement.marginLeft,
                                    // Other
                                    textEffect: updatedElement.textEffect,
                                    order: updatedElement.order,
                                    // Content
                                    contentPreview: updatedElement.content?.substring(0, 80) || '',
                                    allKeys: Object.keys(updatedElement)
                                });
                                
                                // ⚠️ SPECIAL CHECK for H1/H2 (title/subtitle)
                                if (el.elementType === 'title' || el.elementType === 'subtitle') {
                                    console.log(`🚨🚨🚨 H1/H2 ELEMENT MERGED - CHECK THIS:`, {
                                        elementType: updatedElement.elementType,
                                        hasAllProperties: !!(updatedElement.fontSize && updatedElement.fontWeight && updatedElement.maxWidth),
                                        fontSize: updatedElement.fontSize,
                                        fontWeight: updatedElement.fontWeight,
                                        maxWidth: updatedElement.maxWidth,
                                        color: updatedElement.color,
                                        allKeys: Object.keys(updatedElement).join(', ')
                                    });
                                }
                                
                                return updatedElement;
                            }
                            
                            console.log('⚠️ [AI-REFRESH] No match for element:', el.id);
                            return el;
                        });
                    }
                    
                    console.log('🔄 [AI-REFRESH] Final updated elements:', updatedContentElements);
                    console.log('📊 [AI-REFRESH] Element count:', updatedContentElements.length);
                    
                    // ✅ Update widget - contentElements sind in containerSections!
                    let updatedWidget;
                    if (workingData?.containerSections && workingData.containerSections.length > 0) {
                        // ✅ SMART UPDATE: Update Elemente in ihren jeweiligen Containern
                        const updatedContainerSections = workingData.containerSections.map((container, containerIndex) => {
                            // Update nur contentElements die zu diesem Container gehören
                            const updatedContainerElements = (container.contentElements || []).map(el => {
                                // Finde das geupdatete Element
                                const updatedEl = updatedContentElements.find(updated => updated.id === el.id);
                                if (updatedEl) {
                                    console.log('🔄 [AI-REFRESH] Updating element in container', containerIndex, ':', el.id);
                                    
                                    // 🔥 CRITICAL FIX: Remove undefined properties (Firebase würde sie löschen!)
                                    const cleanedElement = Object.keys(updatedEl).reduce((acc, key) => {
                                        if (updatedEl[key] !== undefined) {
                                            acc[key] = updatedEl[key];
                                        }
                                        return acc;
                                    }, {});
                                    
                                    // ⚠️ SPECIAL CHECK for H1/H2
                                    if (cleanedElement.elementType === 'title' || cleanedElement.elementType === 'subtitle') {
                                        console.log(`🚨🚨🚨 WRITING H1/H2 TO CONTAINER [${cleanedElement.elementType?.toUpperCase()}]:`, {
                                            id: cleanedElement.id,
                                            elementType: cleanedElement.elementType,
                                            fontSize: cleanedElement.fontSize,
                                            fontWeight: cleanedElement.fontWeight,
                                            maxWidth: cleanedElement.maxWidth,
                                            color: cleanedElement.color,
                                            fontFamily: cleanedElement.fontFamily,
                                            lineHeight: cleanedElement.lineHeight,
                                            marginTop: cleanedElement.marginTop,
                                            marginBottom: cleanedElement.marginBottom,
                                            contentPreview: cleanedElement.content?.substring(0, 60) || '',
                                            hasAllKeys: Object.keys(cleanedElement).length,
                                            allKeys: Object.keys(cleanedElement).join(', ')
                                        });
                                    }
                                    
                                    return cleanedElement; // ✅ Ohne undefined Properties!
                                }
                                return el; // Unverändert
                            });
                            
                            return {
                                ...container,
                                contentElements: updatedContainerElements
                            };
                        });
                        
                        updatedWidget = {
                            ...data,
                            containerSections: updatedContainerSections,
                            lastAIRefresh: new Date().toISOString()
                        };
                        
                        console.log('✅ [AI-REFRESH] Updated all containers with new content');
                    } else {
                        // Fallback: Direct contentElements
                        updatedWidget = {
                            ...data,
                            contentElements: updatedContentElements,
                            lastAIRefresh: new Date().toISOString()
                        };
                        
                        console.log('✅ [AI-REFRESH] Updated direct contentElements');
                    }
                    
                    onUpdate(updatedWidget);
                    console.log('✅ [AI-REFRESH] Widget updated with new content');
                } else {
                    console.warn('⚠️ [AI-REFRESH] No contentElements in response');
                }
                
                // Show success feedback
                showSuccessToast();
            } else {
                throw new Error(result.error || 'Failed to refresh content');
            }
            
        } catch (error) {
            console.error('❌ [AI-REFRESH] Error refreshing widget content:', error);
            showErrorToast(error.message);
        } finally {
            setIsAIRefreshing(false);
        }
    };

    const showSuccessToast = () => {
        const toast = document.createElement('div');
        toast.className = 'ai-refresh-toast success';
        toast.innerHTML = '✅ Content refreshed with AI!';
        document.body.appendChild(toast);
        
        setTimeout(() => {
            toast.classList.add('fade-out');
            setTimeout(() => document.body.removeChild(toast), 300);
        }, 2000);
    };

    const showErrorToast = (message) => {
        const toast = document.createElement('div');
        toast.className = 'ai-refresh-toast error';
        toast.innerHTML = `❌ AI refresh failed: ${message}`;
        document.body.appendChild(toast);
        
        setTimeout(() => {
            toast.classList.add('fade-out');
            setTimeout(() => document.body.removeChild(toast), 300);
        }, 3000);
    };

    const handleButtonClick = () => {
        if (!isEditMode && data.buttonLink) {
            window.open(data.buttonLink, '_blank');
        }
    };

    // Inline Editing Functions - ORIGINAL BEIBEHALTEN
  const handleInlineEdit = (fieldName, currentValue) => {
    if (!isEditMode || !onUpdate) return;
    
    if (!isSelected && onSelectWidget) {
      onSelectWidget(data.id);
    }
    
    if (isInlineEditing === fieldName) {
      handleInlineCancel();
      return;
    }
    
    setIsInlineEditing(fieldName);
    setInlineValue(currentValue || '');
  };
  
  const handleInlineSubmit = (fieldName) => {
    if (!onUpdate) return;
    
    const updatedData = { ...data, [fieldName]: inlineValue };
    onUpdate(updatedData);
    
    setIsInlineEditing(null);
    setInlineValue('');
  };
  
  const handleInlineCancel = () => {
    setIsInlineEditing(null);
    setInlineValue('');
  };

    // Image Upload Function - ORIGINAL BEIBEHALTEN
    const handleImageUpload = (fieldName, setUploadingState) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file && onUpdate) {
                setUploadingState(true);
                
                setTimeout(() => {
                    const fakeUrl = URL.createObjectURL(file);
                    const updatedData = { ...data, [fieldName]: fakeUrl };
                    onUpdate(updatedData);
                    setUploadingState(false);
                }, 2000);
            }
        };
        input.click();
  };
  
  const handleInlineKeyPress = (e, fieldName) => {
    const isCurrentlyTextarea = e.target.tagName === 'TEXTAREA';
    
    if (e.key === 'Enter') {
      if (isCurrentlyTextarea) {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          handleInlineSubmit(fieldName);
        }
      } else {
        e.preventDefault();
        handleInlineSubmit(fieldName);
      }
    } else if (e.key === 'Escape') {
      handleInlineCancel();
    }
  };
  
    // Render inline editable text - ORIGINAL FUNKTION BEIBEHALTEN
  const renderEditableText = (fieldName, currentValue, Element = 'p', style = {}, placeholder = 'Click to edit...') => {
    if (!isEditMode) {
      if (Element === 'button' && currentValue) {
        return (
          <Element 
            style={style}
            onClick={handleButtonClick}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
            }}
          >
            {currentValue}
          </Element>
        );
      }
      
      return currentValue ? (
        <Element style={{
          ...style,
          whiteSpace: style.whiteSpace || 'pre-wrap'
        }}>
          {currentValue}
        </Element>
      ) : null;
    }

    // 🎨 HOVER INDICATOR für Edit Mode (nicht während Inline-Editing)
    if (isInlineEditing !== fieldName) {
      return (
        <Element 
          style={{
            ...style,
            cursor: Element === 'button' ? 'pointer' : 'text',
            position: 'relative',
            transition: 'all 0.2s ease',
            padding: Element === 'button' ? style.padding : '8px 12px',
            borderRadius: style.borderRadius || (Element === 'button' ? style.borderRadius : '6px'),
            border: Element === 'button' ? style.border : 'none',
            boxShadow: Element === 'button' ? style.boxShadow : 'none',
            display: style.display || 'inline-block',
            whiteSpace: style.whiteSpace || 'pre-wrap'
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const rect = e.target.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(e.target);
            
            const layoutData = {
                width: rect.width + 'px',
                height: rect.height + 'px',
                marginTop: computedStyle.marginTop,
                marginBottom: computedStyle.marginBottom,
                marginLeft: computedStyle.marginLeft,
                marginRight: computedStyle.marginRight
            };
            
            setOriginalLayout(layoutData);
            handleInlineEdit(fieldName, currentValue);
          }}
          onMouseEnter={(e) => {
            // ✅ HOVER EFFEKT: Dezenter Rahmen + Background
            e.target.style.outline = '2px dashed rgba(99, 102, 241, 0.5)';
            e.target.style.outlineOffset = '2px';
            e.target.style.backgroundColor = 'rgba(99, 102, 241, 0.05)';
          }}
          onMouseLeave={(e) => {
            // ✅ HOVER EFFEKT entfernen
            e.target.style.outline = 'none';
            e.target.style.outlineOffset = '0px';
            e.target.style.backgroundColor = Element === 'button' ? (style.backgroundColor || style.background) : 'transparent';
          }}
          title="Click to edit"
        >
          {currentValue || placeholder}
        </Element>
      );
    }

    if (isInlineEditing === fieldName) {
            const layoutData = originalLayout;
      const isTextarea = Element !== 'button';
            
            const renderActionButtons = () => (
                <div style={{
                    position: 'absolute',
                    top: Element === 'button' ? '-11px' : '-11px',
                    right: Element === 'button' ? '-6px' : '-6px',
                    display: 'flex',
                    gap: '4px',
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '2px 4px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                }}>
                    <button
                        data-action-button="save"
                        onClick={() => handleInlineSubmit(fieldName)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(16, 185, 129, 0.3)'
                        }}
                        title="Save"
                    >
                        ✓
                    </button>
                    
                    <button
                        data-action-button="cancel"
                        onClick={handleInlineCancel}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(239, 68, 68, 0.3)'
                        }}
                        title="Cancel"
                    >
                        ×
                    </button>
                </div>
            );
      
      return isTextarea ? (
                <div style={{ 
                    position: 'relative',
                    width: '100%',
                    marginTop: layoutData.marginTop || '0',
                    marginBottom: layoutData.marginBottom || '0',
                    marginLeft: layoutData.marginLeft || '0',
                    marginRight: layoutData.marginRight || '0'
                }}>
          <textarea
            value={inlineValue}
                        onChange={(e) => {
                            setInlineValue(e.target.value);
                            const target = e.target;
                            const cursorPosition = target.selectionStart;
                            setTimeout(() => {
                                target.style.height = 'auto';
                                target.style.height = target.scrollHeight + 'px';
                                target.setSelectionRange(cursorPosition, cursorPosition);
                            }, 0);
                        }}
                        onKeyDown={(e) => {
                            handleInlineKeyPress(e, fieldName);
                            
                            if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
                                setTimeout(() => {
                                    const target = e.target;
                                    target.style.height = 'auto';
                                    target.style.height = target.scrollHeight + 'px';
                                }, 0);
                            }
                        }}
            onBlur={() => handleInlineSubmit(fieldName)}
            placeholder={placeholder}
            autoFocus
            ref={(element) => {
                if (element) {
                    // ✅ KRITISCH: Cursor ans ENDE setzen
                    setTimeout(() => {
                        element.style.height = 'auto';
                        element.style.height = element.scrollHeight + 'px';
                        element.setSelectionRange(element.value.length, element.value.length);
                    }, 0);
                }
            }}
            style={{
              ...style,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              resize: 'none',
              padding: '4px 8px',
              margin: '0',
              width: '100%',
                            minHeight: layoutData.height || 'auto',
              color: '#333333',
              backgroundImage: 'none',
              backgroundClip: 'unset',
              webkitBackgroundClip: 'unset',
              webkitTextFillColor: '#333333',
                            fontFamily: style.fontFamily,
                            fontSize: style.fontSize,
                            fontWeight: style.fontWeight,
                            lineHeight: style.lineHeight,
                            letterSpacing: style.letterSpacing,
                            textAlign: style.textAlign || 'inherit',
                            whiteSpace: style.whiteSpace || 'pre-wrap'
                        }}
                    />
                    {renderActionButtons()}
        </div>
      ) : (
        <div style={{ 
            position: 'relative',
            width: layoutData.width || '100%',
            marginTop: layoutData.marginTop || '0',
            marginBottom: layoutData.marginBottom || '0',
            marginLeft: layoutData.marginLeft || '0',
            marginRight: layoutData.marginRight || '0'
        }}>
            <input
              type="text"
              value={inlineValue}
              onChange={(e) => setInlineValue(e.target.value)}
              onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
              onBlur={() => handleInlineSubmit(fieldName)}
              placeholder={placeholder}
              autoFocus
              ref={(element) => {
                  if (element) {
                      // ✅ KRITISCH: Cursor ans ENDE setzen für Input-Felder
                      setTimeout(() => {
                          element.setSelectionRange(element.value.length, element.value.length);
                      }, 0);
                  }
              }}
              style={{
                ...style,
                background: style.background || style.backgroundColor,
                border: style.border || 'none',
                borderRadius: style.borderRadius,
                padding: style.padding,
                width: layoutData.width || style.width,
                height: layoutData.height || style.height,
                minWidth: layoutData.width || style.minWidth,
                maxWidth: layoutData.width || 'none',
                minHeight: layoutData.height || 'auto',
                maxHeight: layoutData.height || 'none',
                marginTop: '0',
                marginBottom: '0',
                marginLeft: '0',
                marginRight: '0',
                display: style.display,
                alignItems: style.alignItems,
                justifyContent: style.justifyContent,
                alignSelf: style.alignSelf,
                boxShadow: 'none',
                cursor: 'text',
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            />
            {renderActionButtons()}
        </div>
      );
    }

    const handleElementClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
            
            const rect = e.target.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(e.target);
            
            const layoutData = {
                width: rect.width + 'px',
                height: rect.height + 'px',
                marginTop: computedStyle.marginTop,
                marginBottom: computedStyle.marginBottom,
                marginLeft: computedStyle.marginLeft,
                marginRight: computedStyle.marginRight
            };
            
            setOriginalLayout(layoutData);
      handleInlineEdit(fieldName, currentValue);
    };

    return (
      <Element 
        style={{
          ...style,
          cursor: Element === 'button' ? 'pointer' : 'text',
          position: 'relative',
          transition: 'all 0.2s ease',
          padding: Element === 'button' ? style.padding : '4px 8px',
          borderRadius: style.borderRadius || (Element === 'button' ? style.borderRadius : '4px'),
          border: Element === 'button' ? style.border : 'none',
                    boxShadow: Element === 'button' ? style.boxShadow : 'none'
        }}
        onClick={handleElementClick}
        title="Click to edit"
      >
        {currentValue || placeholder}
                {isEditMode && isInlineEditing === fieldName && (
                    <span style={{
                        display: 'inline',
                        animation: 'blink 1s infinite',
                        fontSize: 'inherit',
                        color: 'inherit',
                        marginLeft: '1px'
                    }}>
                        |
                    </span>
                )}
      </Element>
    );
  };

    // Image Effect Helper - ORIGINAL BEIBEHALTEN
    const getImageEffect = (effect, blurIntensity = 0) => {
        let baseEffect = {};
        
        switch (effect) {
            case 'darken-light':
                baseEffect = { filter: 'brightness(0.8)' };
                break;
            case 'darken':
                baseEffect = { filter: 'brightness(0.6)' };
                break;
            case 'darken-heavy':
                baseEffect = { filter: 'brightness(0.4)' };
                break;
            case 'darken-extreme':
                baseEffect = { filter: 'brightness(0.2)' };
                break;
            case 'lighten-light':
                baseEffect = { filter: 'brightness(1.2)' };
                break;
            case 'lighten':
                baseEffect = { filter: 'brightness(1.4)' };
                break;
            case 'lighten-heavy':
                baseEffect = { filter: 'brightness(1.6)' };
                break;
            case 'lighten-extreme':
                baseEffect = { filter: 'brightness(1.8)' };
                break;
            case 'vignette-light':
                baseEffect = { 
                    filter: 'none',
                    background: 'radial-gradient(circle, transparent 60%, rgba(0,0,0,0.2))',
                    backgroundBlendMode: 'multiply'
                };
                break;
            case 'vignette':
                baseEffect = { 
                    filter: 'none',
                    background: 'radial-gradient(circle, transparent 50%, rgba(0,0,0,0.4))',
                    backgroundBlendMode: 'multiply'
                };
                break;
            case 'vignette-heavy':
                baseEffect = { 
                    filter: 'none',
                    background: 'radial-gradient(circle, transparent 40%, rgba(0,0,0,0.6))',
                    backgroundBlendMode: 'multiply'
                };
                break;
            case 'vignette-extreme':
                baseEffect = { 
                    filter: 'none',
                    background: 'radial-gradient(circle, transparent 30%, rgba(0,0,0,0.8))',
                    backgroundBlendMode: 'multiply'
                };
                break;
            case 'grayscale':
                baseEffect = { filter: 'grayscale(100%)' };
                break;
            case 'sepia':
                baseEffect = { filter: 'sepia(100%)' };
                break;
            default:
                baseEffect = { filter: 'none' };
                break;
        }
        
        if (blurIntensity > 0) {
            const existingFilter = baseEffect.filter || 'none';
            const blurFilter = `blur(${blurIntensity}px)`;
            
            baseEffect.filter = existingFilter === 'none' ? blurFilter : `${existingFilter} ${blurFilter}`;
            
            if (blurIntensity >= 3) {
                const scaleValue = 1 + (blurIntensity * 0.02);
                const marginValue = -(blurIntensity * 1);
                
                baseEffect.transform = `scale(${scaleValue})`;
                baseEffect.margin = `${marginValue}%`;
            }
        }
        
        return baseEffect;
    };

    // 🎨 Helper: Konvertiert Hex zu RGBA mit Opacity
    const hexToRgba = (hex, opacity = 1) => {
        // Entferne # wenn vorhanden
        hex = hex.replace('#', '');
        
        // Parse hex values
        let r, g, b;
        if (hex.length === 3) {
            r = parseInt(hex[0] + hex[0], 16);
            g = parseInt(hex[1] + hex[1], 16);
            b = parseInt(hex[2] + hex[2], 16);
        } else {
            r = parseInt(hex.substring(0, 2), 16);
            g = parseInt(hex.substring(2, 4), 16);
            b = parseInt(hex.substring(4, 6), 16);
        }
        
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    };

    // Dynamic Layout Styles - ORIGINAL FUNKTION BEIBEHALTEN
    const getContainerStyles = () => {
        const baseStyles = {
            ...widgetStyle,
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            
            // 🎨 LAYOUT TOOLBAR: Apply Background (Color, Gradient, Image)
            ...((() => {
                // Background Image hat Priorität
                if (data.backgroundImage) {
                    return {
                        backgroundImage: `url(${data.backgroundImage})`,
                        backgroundSize: data.backgroundImageSize || 'cover',
                        backgroundPosition: data.backgroundImagePosition || 'center',
                        backgroundRepeat: 'no-repeat',
                        opacity: (data.backgroundImageOpacity ?? 100) / 100
                    };
                }
                // Gradient mit Opacity direkt in den Farben
                if (data.backgroundGradientEnabled) {
                    const color1 = data.backgroundGradientColor1 || '#667eea';
                    const color2 = data.backgroundGradientColor2 || '#764ba2';
                    const opacityValue = (data.backgroundOpacity ?? 100) / 100;
                    
                    // ✅ Konvertiere Farben zu RGBA mit Opacity
                    const rgba1 = hexToRgba(color1, opacityValue);
                    const rgba2 = hexToRgba(color2, opacityValue);
                    
                    // 🎯 Gradient Direction Helper
                    const getGradientDirection = () => {
                        const direction = data.backgroundGradientDirection || 'to-bottom-right';
                        
                        // ✅ NEW: CSS Gradient Directions (to bottom, to right, etc.)
                        const directionMap = {
                            'to-bottom': 'to bottom',
                            'to-right': 'to right',
                            'to-top': 'to top',
                            'to-left': 'to left',
                            'to-bottom-right': '135deg',
                            'to-bottom-left': '225deg',
                            'to-top-right': '45deg',
                            'to-top-left': '315deg',
                            // ✅ BACKWARD COMPATIBILITY: Old values (for animation)
                            'horizontal': 'to right',
                            'vertical': 'to bottom',
                            'diagonal-left': '135deg',
                            'diagonal-right': '45deg'
                        };
                        
                        return directionMap[direction] || '135deg';
                    };
                    
                    // 🎯 Animation basierend auf Direction (nur für alte Werte)
                    const getGradientAnimation = () => {
                        if (!data.backgroundGradientAnimation) return {};
                        
                        const direction = data.backgroundGradientDirection || 'horizontal';
                        const animationMap = {
                            'horizontal': 'gradientShift 3s ease infinite',
                            'to-right': 'gradientShift 3s ease infinite',
                            'vertical': 'gradientShiftVertical 3s ease infinite',
                            'to-bottom': 'gradientShiftVertical 3s ease infinite',
                            'diagonal-left': 'gradientShiftDiagonalLeft 3s ease infinite',
                            'to-bottom-right': 'gradientShiftDiagonalLeft 3s ease infinite',
                            'diagonal-right': 'gradientShiftDiagonalRight 3s ease infinite',
                            'to-top-right': 'gradientShiftDiagonalRight 3s ease infinite'
                        };
                        
                        return {
                            backgroundSize: '200% 200%',
                            animation: animationMap[direction] || animationMap['horizontal']
                        };
                    };
                    
                    return {
                        background: `linear-gradient(${getGradientDirection()}, ${rgba1} 0%, ${rgba2} 100%)`,
                        ...getGradientAnimation()
                    };
                }
                // Solid Color mit Opacity direkt in der Farbe
                const bgColor = data.backgroundColor || '#ffffff';
                const opacityValue = (data.backgroundOpacity ?? 100) / 100;
                const rgbaColor = hexToRgba(bgColor, opacityValue);
                
                return {
                    backgroundColor: rgbaColor
                };
            })()),
            
            // 🎨 LAYOUT TOOLBAR: Apply Padding (Desktop/Mobile)
            paddingTop: isMobileLayout 
                ? `${data.paddingTopMobile ?? data.paddingTop ?? 0}px`
                : `${data.paddingTop ?? 0}px`,
            paddingRight: isMobileLayout
                ? `${data.paddingRightMobile ?? data.paddingRight ?? 0}px`
                : `${data.paddingRight ?? 0}px`,
            paddingBottom: isMobileLayout
                ? `${data.paddingBottomMobile ?? data.paddingBottom ?? 0}px`
                : `${data.paddingBottom ?? 0}px`,
            paddingLeft: isMobileLayout
                ? `${data.paddingLeftMobile ?? data.paddingLeft ?? 0}px`
                : `${data.paddingLeft ?? 0}px`,
            
            // 🎨 LAYOUT TOOLBAR: Apply Margin (Desktop/Mobile)
            marginTop: isMobileLayout
                ? `${data.marginTopMobile ?? data.marginTop ?? 0}px`
                : `${data.marginTop ?? 0}px`,
            marginRight: isMobileLayout
                ? `${data.marginRightMobile ?? data.marginRight ?? 0}px`
                : `${data.marginRight ?? 0}px`,
            marginBottom: isMobileLayout
                ? `${data.marginBottomMobile ?? data.marginBottom ?? 0}px`
                : `${data.marginBottom ?? 0}px`,
            marginLeft: isMobileLayout
                ? `${data.marginLeftMobile ?? data.marginLeft ?? 0}px`
                : `${data.marginLeft ?? 0}px`,
            
            // 🎨 LAYOUT TOOLBAR: Apply Dimensions (Desktop/Mobile)
            // Width
            ...((() => {
                const widthValue = isMobileLayout 
                    ? (data.widthMobile ?? data.width)
                    : data.width;
                if (widthValue && widthValue !== 'auto') {
                    return { width: `${widthValue}px` };
                }
                return {};
            })()),
            
            // Height
            ...((() => {
                const heightValue = isMobileLayout 
                    ? (data.heightMobile ?? data.height)
                    : data.height;
                if (heightValue && heightValue !== 'auto') {
                    return { height: `${heightValue}px` };
                }
                return {};
            })()),
            
            // Min-Width
            ...((() => {
                const minWidthValue = isMobileLayout 
                    ? (data.minWidthMobile ?? data.minWidth)
                    : data.minWidth;
                if (minWidthValue && minWidthValue !== 'auto') {
                    return { minWidth: `${minWidthValue}px` };
                }
                return {};
            })()),
            
            // Min-Height
            ...((() => {
                const minHeightValue = isMobileLayout 
                    ? (data.minHeightMobile ?? data.minHeight)
                    : data.minHeight;
                if (minHeightValue && minHeightValue !== 'auto') {
                    return { minHeight: `${minHeightValue}px` };
                }
                return {};
            })()),
            
            // 🎨 BORDER - Individual borders per side
            ...((() => {
                const borders = {};
                
                if (data.borderTopEnabled) {
                    borders.borderTop = `${data.borderTopWidth || 1}px solid ${data.borderTopColor || '#cccccc'}`;
                }
                if (data.borderBottomEnabled) {
                    borders.borderBottom = `${data.borderBottomWidth || 1}px solid ${data.borderBottomColor || '#cccccc'}`;
                }
                if (data.borderLeftEnabled) {
                    borders.borderLeft = `${data.borderLeftWidth || 1}px solid ${data.borderLeftColor || '#cccccc'}`;
                }
                if (data.borderRightEnabled) {
                    borders.borderRight = `${data.borderRightWidth || 1}px solid ${data.borderRightColor || '#cccccc'}`;
                }
                
                return borders;
            })()),
        };

        // Handle overlay mode - verwendet Background Image statt Hero Image
        if (layoutConfig.overlayMode) {
            const imageEffect = getImageEffect(data.backgroundImageEffect || 'none', data.backgroundImageBlur || 0);
            const isVignetteEffect = data.backgroundImageEffect?.includes('vignette');
            
            // ✅ FIX: Background Image mit korrekter Opacity und Effects
            const backgroundOpacity = isVignetteEffect ? 1 : ((data.backgroundImageOpacity || 50) / 100);
            
            return {
                ...baseStyles,
                // ✅ PROBLEM GELÖST: Background Image wird jetzt über separates Pseudo-Element mit Opacity gehandhabt
                position: 'relative',
                minHeight: data.height === 'auto' ? 'auto' : appendPx(isMobileLayout ? 
                    (data.heightMobile || 1100) :  // Mobile: Template-Default 1100px
                    (data.height || 650)           // Desktop: Template-Default 650px
                ),
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            };
        }

        const shouldStack = isMobileLayout && layoutConfig.mobileStack;
        
        // 🎨 LAYOUT TOOLBAR: Apply Flex Direction (responsive)
        const finalFlexDirection = isMobileLayout
            ? (data.flexDirectionMobile ?? data.flexDirection ?? (shouldStack ? 'column' : layoutConfig.direction))
            : (data.flexDirection ?? (shouldStack ? 'column' : layoutConfig.direction));
        
        // ✅ FIX: Default minHeight für Row-Layout (damit Image Container stretched werden kann)
        const hasUserMinHeight = isMobileLayout 
            ? (data.minHeightMobile && data.minHeightMobile !== 'auto')
            : (data.minHeight && data.minHeight !== 'auto');
        
        const isRowLayout = finalFlexDirection === 'row' || finalFlexDirection === 'row-reverse';
        const defaultMinHeight = isRowLayout && !hasUserMinHeight && !isMobileLayout ? '50px' : undefined;
        
        return {
            ...baseStyles,
            flexDirection: finalFlexDirection,
            ...(defaultMinHeight && { minHeight: defaultMinHeight }) // ✅ Default minHeight für Row-Layout
        };
  };

  return (
    <>
            {/* CSS Animations - ORIGINAL BEIBEHALTEN */}
      <style>
        {`
          /* ✅ LIVE PAGE RESPONSIVE FIX: Kontinuierliche Viewport-Anpassung */
          .hero-header {
            --effective-width: ${containerRef.current?.style.getPropertyValue('--effective-width') || '100%'};
            --viewport-width: ${containerRef.current?.style.getPropertyValue('--viewport-width') || '100vw'};
          }
          
          /* Live Page: Responsive Text Scaling basierend auf Viewport */
          @media (max-width: 750px) {
            .hero-header {
              max-width: var(--viewport-width, 100vw) !important;
              width: var(--viewport-width, 100vw) !important;
            }
            
            .hero-header * {
              max-width: 100% !important;
              word-wrap: break-word !important;
              overflow-wrap: break-word !important;
            }
            
            /* Responsive Font Scaling für sehr kleine Viewports */
            .hero-header h1,
            .hero-header h2,
            .hero-header .hero-title {
              font-size: clamp(1.5rem, 4vw, 3rem) !important;
            }
            
            .hero-header p,
            .hero-header .hero-description {
              font-size: clamp(0.9rem, 3vw, 1.2rem) !important;
            }
            
            .hero-header button {
              font-size: clamp(0.8rem, 2.5vw, 1rem) !important;
              padding: clamp(8px, 2vw, 16px) clamp(16px, 4vw, 32px) !important;
            }
          }
        
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
                    
                    @keyframes silverShimmer {
                        0% { 
                            background-position: -200% center;
                        }
                        100% { 
                            background-position: 200% center;
                        }
          }
          
          @keyframes heroImageUploadSpin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
                    
                    @keyframes silverGlow {
                        0% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.02) 0%,
                                rgba(192, 192, 192, 0.03) 25%,
                                rgba(255, 255, 255, 0.04) 50%,
                                rgba(192, 192, 192, 0.03) 75%,
                                rgba(255, 255, 255, 0.02) 100%
                            );
                        }
                        50% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.04) 0%,
                                rgba(192, 192, 192, 0.05) 25%,
                                rgba(255, 255, 255, 0.06) 50%,
                                rgba(192, 192, 192, 0.05) 75%,
                                rgba(255, 255, 255, 0.04) 100%
                            );
                        }
                        100% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.02) 0%,
                                rgba(192, 192, 192, 0.03) 25%,
                                rgba(255, 255, 255, 0.04) 50%,
                                rgba(192, 192, 192, 0.03) 75%,
                                rgba(255, 255, 255, 0.02) 100%
                            );
                        }
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateX(-30px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
                    
                    @keyframes fadeInSlow {
                        0% { opacity: 0; }
                        30% { opacity: 0; }
                        100% { opacity: 1; }
          }
          
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
                    
                    @keyframes gentle-pulse {
                        0%, 100% { 
                            transform: scale(1); 
                            opacity: 1; 
                        }
                        50% { 
                            transform: scale(1.02); 
                            opacity: 0.9; 
                        }
                    }

                    @keyframes gentle-sway {
                        0%, 100% { 
                            transform: translateX(0px); 
                        }
                        25% { 
                            transform: translateX(-2px); 
                        }
                        75% { 
                            transform: translateX(2px); 
                        }
                    }

                    @keyframes subtle-float {
                        0%, 100% { 
                            transform: translateY(0px); 
                        }
                        50% { 
                            transform: translateY(-3px); 
                        }
                    }

                    @keyframes gentle-breathe {
                        0%, 100% { 
                            transform: scale(1); 
                            opacity: 1; 
                        }
                        50% { 
                            transform: scale(1.03); 
                            opacity: 0.85; 
                        }
                    }

                    @keyframes gentle-wobble {
                        0%, 100% { 
                            transform: rotate(0deg); 
                        }
                        25% { 
                            transform: rotate(-1deg); 
                        }
                        75% { 
                            transform: rotate(1deg); 
                        }
          }
          
          .hero-animated {
            animation-fill-mode: both;
          }
          
          .hero-animated.fadeInUp {
            animation-name: fadeInUp;
          }
          
          .hero-animated.slideIn {
            animation-name: slideIn;
          }
          
          .hero-animated.fadeIn {
            animation-name: fadeIn;
          }
                    
                    .hero-animated.fadeInSlow {
                        animation-name: fadeInSlow;
          }
        `}
      </style>
            <div 
        className={`widget-container hero-header-widget layout-${data.layoutType || 'classic-split'} ${isEditMode ? 'edit-mode' : 'live-mode'}`}
                style={{
                    ...getContainerStyles(),
                    // ✅ Dynamic flexDirection & justifyContent basierend auf Layout Settings
                    flexDirection: (() => {
                        const activeDir = isMobileLayout 
                            ? (data.flexDirectionMobile ?? data.flexDirection ?? 'row')
                            : (data.flexDirection ?? 'row');
                        
                        // ✅ NEW: row-left, row, row-right werden alle als 'row' gerendert
                        // (Ausrichtung wird via justifyContent gesteuert)
                        if (activeDir === 'row-left' || activeDir === 'row' || activeDir === 'row-right') {
                            return 'row';
                        }
                        
                        return activeDir; // column, row-reverse, column-reverse
                    })(),
                    justifyContent: (() => {
                        const activeDir = isMobileLayout 
                            ? (data.flexDirectionMobile ?? data.flexDirection ?? 'row')
                            : (data.flexDirection ?? 'row');
                        
                        // ✅ NEW: row-left, row, row-right für Single Container Alignment
                        if (activeDir === 'row-left') return 'flex-start';
                        if (activeDir === 'row-right') return 'flex-end';
                        if (activeDir === 'row' || activeDir === 'column') return 'center';
                        
                    // Defaults für andere Directions
                    return 'center';
                })(),
                // ✅ Gap zwischen Containern
                gap: '0px'
                }} 
        ref={(el) => {
                    containerRef.current = el;
          widgetRef.current = el;
        }}
      >
            {/* 🚀 BACKGROUND IMAGE für Full Width Overlay mit korrekter Opacity */}
            {layoutConfig.overlayMode && data.backgroundImage && (() => {
                const imageEffect = getImageEffect(data.backgroundImageEffect || 'none', data.backgroundImageBlur || 0);
                const isVignetteEffect = data.backgroundImageEffect?.includes('vignette');
                const backgroundOpacity = isVignetteEffect ? 1 : ((data.backgroundImageOpacity || 50) / 100);
                
                return (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 0, // Hinter dem Content
                        pointerEvents: 'none'
                    }}>
                        {/* Background Image mit Opacity */}
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            width: '100%',
                            height: '100%',
                            backgroundImage: `url(${data.backgroundImage})`,
                            backgroundSize: data.backgroundImageSize || 'cover',
                            backgroundPosition: data.backgroundImagePosition || 'center',
                            backgroundRepeat: 'no-repeat',
                            opacity: backgroundOpacity, // ✅ OPACITY FUNKTIONIERT JETZT!
                            filter: imageEffect.filter || 'none',
                            transform: imageEffect.transform || 'none',
                            margin: imageEffect.margin || '0',
                            transition: 'opacity 0.4s ease-in-out'
                        }} />
                        
                        {/* Vignette Overlay (nur bei Vignette Effects) */}
                        {isVignetteEffect && imageEffect.background && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                width: '100%',
                                height: '100%',
                                background: imageEffect.background,
                                backgroundBlendMode: imageEffect.backgroundBlendMode || 'multiply',
                                pointerEvents: 'none'
                            }} />
                        )}
                    </div>
                );
            })()}

            {/* 🎯 3-Dots Widget Menu */}
            {isEditMode && (
                <div className="widget-menu-container">
                    <button 
                        className={`widget-menu-button ${isAIRefreshing ? 'ai-loading' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!isAIRefreshing) {
                                setShowWidgetMenu(!showWidgetMenu);
                            }
                        }}
                        title={isAIRefreshing ? 'Generating AI content...' : 'Widget Options'}
                        disabled={isAIRefreshing}
                    >
                        {isAIRefreshing ? (
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="ai-loading-icon">
                                <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.3"/>
                                <path d="M10 2 A 8 8 0 0 1 18 10" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round">
                                    <animateTransform
                                        attributeName="transform"
                                        type="rotate"
                                        from="0 10 10"
                                        to="360 10 10"
                                        dur="1s"
                                        repeatCount="indefinite"/>
                                </path>
                                <circle cx="10" cy="10" r="3" fill="currentColor">
                                    <animate
                                        attributeName="opacity"
                                        values="0.5;1;0.5"
                                        dur="1.5s"
                                        repeatCount="indefinite"/>
                                </circle>
                            </svg>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <circle cx="10" cy="4" r="1.5" fill="currentColor"/>
                                <circle cx="10" cy="10" r="1.5" fill="currentColor"/>
                                <circle cx="10" cy="16" r="1.5" fill="currentColor"/>
                            </svg>
                        )}
                    </button>
                    
                    {showWidgetMenu && (
                        <div ref={widgetMenuRef} className="widget-dropdown-menu">
                            {/* Layout Settings Button - Ganz oben */}
                            <button 
                                className="widget-menu-item"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowWidgetMenu(false);
                                    setCurrentlyOpenToolbar(isLayoutToolbarVisible ? null : 'layout');
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                    <line x1="8" y1="2" x2="8" y2="14" stroke="currentColor" strokeWidth="1.5"/>
                                    <line x1="2" y1="8" x2="14" y2="8" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                                <span>Layout Settings</span>
                            </button>
                            
                            {/* Duplicate Widget Button */}
                            <button 
                                className="widget-menu-item"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowWidgetMenu(false);
                                    if (onDuplicate) {
                                        onDuplicate();
                                    }
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <rect x="3" y="3" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                    <path d="M5 3V2a1 1 0 011-1h8a1 1 0 011 1v8a1 1 0 01-1 1h-1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                </svg>
                                <span>Duplicate Widget</span>
                            </button>
                            
                            {/* Divider vor AI */}
                            <div className="widget-menu-divider"></div>
                            
                            {/* AI Generate Button - Normales Item mit star.svg */}
                            <button 
                                className="widget-menu-item widget-menu-item-ai"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowWidgetMenu(false);
                                    setShowAIPromptPopup(true);
                                }}
                                disabled={isAIRefreshing}
                            >
                                {isAIRefreshing ? (
                                    <>
                                        <div className="ai-icon-wrapper ai-loading">
                                            <img src="/img/star.svg" alt="AI" />
                                        </div>
                                        <span>Generating...</span>
                                    </>
                                ) : (
                                    <>
                                        <div className="ai-icon-wrapper">
                                            <img src="/img/star.svg" alt="AI" />
                                        </div>
                                        <span>Text with AI</span>
                                    </>
                                )}
                            </button>
                            
                            {/* Add Container Section - bis zu 10 Container möglich */}
                            {(() => {
                                console.log('🔍 [ADD SECTION BUTTON] containerSections:', containerSections);
                                console.log('🔍 [ADD SECTION BUTTON] Length:', containerSections?.length);
                                console.log('🔍 [ADD SECTION BUTTON] Show button?', containerSections && containerSections.length < 10);
                                return null;
                            })()}
                            {containerSections && containerSections.length < 10 && (
                                <>
                                    <div className="widget-menu-divider"></div>
                                    
                                    <button 
                                        className="widget-menu-item"
                                        onClick={(e) => {
                                            console.log('🎯 [ADD SECTION BUTTON] CLICKED!');
                                            e.stopPropagation();
                                            handleAddContainer('text');
                                            setShowWidgetMenu(false);
                                        }}
                                    >
                                        <i className="fas fa-plus-circle"></i>
                                        <span>Add Section</span>
                                    </button>
                                </>
                            )}
                            
                            <div className="widget-menu-divider"></div>
                            <button 
                                className="widget-menu-item widget-menu-item-danger"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowWidgetMenu(false);
                                    setShowDeleteModal(true);
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M3.5 5h9M5.5 5V3.5a1 1 0 011-1h3a1 1 0 011 1V5m1.5 0v8.5a1 1 0 01-1 1h-6a1 1 0 01-1-1V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span>Delete Widget</span>
                            </button>
                        </div>
                    )}
                </div>
            )}
            {isEditMode && showDeleteModal && (
              <DeleteModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={(event) => handleDelete(event, data.id)}
              />
            )}

            {/* 🚀 DYNAMIC CONTAINER SECTIONS 2025 */}
            {containerSections && containerSections.length > 0 && containerSections.map((container, index) => {
                // 🎨 Calculate responsive flexDirection
                const shouldStack = isMobileLayout && layoutConfig.mobileStack;
                const activeFlexDirection = isMobileLayout
                    ? (data.flexDirectionMobile ?? data.flexDirection ?? (shouldStack ? 'column' : layoutConfig.direction))
                    : (data.flexDirection ?? (shouldStack ? 'column' : layoutConfig.direction));
                
                // 🎨 Calculate flex basis based on responsive childDistribution
                const activeDistribution = isMobileLayout
                    ? (data.childDistributionMobile ?? data.childDistribution ?? '50-50')
                    : (data.childDistribution ?? '50-50');
                
                // Convert distribution to grid layout
                const distributionToGridLayout = {
                    // Single container widths (centered)
                    '100': 'grid-cols-1-100',
                    '90': 'grid-cols-1-90',
                    '80': 'grid-cols-1-80',
                    '70': 'grid-cols-1-70',
                    '60': 'grid-cols-1-60',
                    '50': 'grid-cols-1-50',
                    // Two column layouts
                    '50-50': 'grid-cols-2-equal',
                    '60-40': 'grid-cols-2-60-40',
                    '40-60': 'grid-cols-2-40-60',
                    '70-30': 'grid-cols-2-70-30',
                    '30-70': 'grid-cols-2-30-70',
                    // Three column layouts
                    '33-33-33': 'grid-cols-3-equal',
                    '50-25-25': 'grid-cols-3-50-25-25',
                    '25-50-25': 'grid-cols-3-25-50-25',
                    '25-25-50': 'grid-cols-3-25-25-50',
                    // Four column layouts
                    '25-25-25-25': 'grid-cols-4-equal',
                    '40-20-20-20': 'grid-cols-4-40-20-20-20',
                    // Five column layout
                    '20-20-20-20-20': 'grid-cols-5-equal',
                    'auto': 'grid-cols-2-equal'
                };
                
                const activeGridLayout = distributionToGridLayout[activeDistribution] || workingData.gridLayout || 'grid-cols-2-equal';
                const flexBasis = getGridFlexBasis(activeGridLayout, index);
                
                // ✅ Debug Log
                // if (index === 0) {
                //     console.log('🎯 [CONTAINER FLEX]', {
                //         containerIndex: index,
                //         activeDistribution,
                //         activeGridLayout,
                //         flexBasis,
                //         isMobileLayout,
                //         activeFlexDirection
                //     });
                // }
                
                // ✅ Bei column/column-reverse: Distribution für Höhe verwenden
                // ✅ Bei row/row-reverse: Distribution für Breite verwenden
                const isVerticalLayout = activeFlexDirection === 'column' || activeFlexDirection === 'column-reverse';
                
                return (
                    <div
                        key={container.id}
                        className="dynamic-container-wrapper"
                        style={{
                            // ✅ Distribution wird immer angewendet, nur die Richtung ändert sich
                            flex: flexBasis,
                            minWidth: 0, // Prevent flex overflow
                            order: container.order,
                            // ✅ Bei row: width durch flex, height auto
                            // ✅ Bei column: height durch flex, width 100%
                            ...(isVerticalLayout ? {
                                width: '100%'
                            } : {})
                        }}
                        ref={(el) => {
                            if (el) {
                                containerRefsArray.current[index] = el;
                            }
                        }}
                    >
                        <DynamicContainerSection
                            container={
                                // ⚡ OPTIMISTIC: Merge container mit optimistic styles für sofortiges Feedback
                                containerOptimisticStyles[container.id] 
                                    ? { ...container, ...containerOptimisticStyles[container.id] }
                                    : container
                            }
                            containerIndex={index}
                            totalContainers={containerSections.length}
                            isEditMode={isEditMode}
                            isMobileLayout={isMobileLayout}
                            onUpdate={handleContainerUpdate}
                            onDelete={handleContainerDelete}
                            onStyleUpdate={handleContainerUpdate}
                            isToolbarVisible={currentlyOpenToolbar === `container-${index}`}
                            onToolbarToggle={() => setCurrentlyOpenToolbar(
                                currentlyOpenToolbar === `container-${index}` ? null : `container-${index}`
                            )}
                            colors={colors}
                            colorAssignments={colorAssignments}
                            fonts={activeFonts}
                            availableFonts={availableFonts}
                            layoutConfig={layoutConfig}
                            currentlyEditingElementId={currentlyEditingElementId}
                            onEditingChange={setCurrentlyEditingElementId}
                            containerRef={(el) => {
                                if (el) {
                                    containerRefsArray.current[index] = el;
                                }
                            }}
                            defaultHeroImage={defaultHeroImage}
                        />
                    </div>
                );
            })}
            
            {/* 🎨 Container Floating Toolbars - Dynamic for all containers */}
            {isEditMode && containerSections && containerSections.map((container, index) => {
                // ⚡ OPTIMISTIC CONTAINER: Merge container mit optimistic styles
                const optimisticContainer = containerOptimisticStyles[container.id] 
                    ? { ...container, ...containerOptimisticStyles[container.id] }
                    : container;
                
                return (
                <ContainerFloatingToolbar
                    key={`toolbar-${container.id}`}
                    isEditMode={isEditMode}
                    isVisible={currentlyOpenToolbar === `container-${index}`}
                    onClose={() => setCurrentlyOpenToolbar(null)}
                    containerRef={containerRefsArray.current[index] ? { current: containerRefsArray.current[index] } : null}
                    
                    // Padding (Desktop) - ⚡ OPTIMISTIC
                    paddingTop={optimisticContainer.containerPaddingTop}
                    paddingRight={optimisticContainer.containerPaddingRight}
                    paddingBottom={optimisticContainer.containerPaddingBottom}
                    paddingLeft={optimisticContainer.containerPaddingLeft}
                    
                    // Padding (Mobile) - ⚡ OPTIMISTIC
                    paddingTopMobile={optimisticContainer.containerPaddingTopMobile}
                    paddingRightMobile={optimisticContainer.containerPaddingRightMobile}
                    paddingBottomMobile={optimisticContainer.containerPaddingBottomMobile}
                    paddingLeftMobile={optimisticContainer.containerPaddingLeftMobile}
                    
                    // Margin (Desktop) - ⚡ OPTIMISTIC
                    marginTop={optimisticContainer.containerMarginTop}
                    marginRight={optimisticContainer.containerMarginRight}
                    marginBottom={optimisticContainer.containerMarginBottom}
                    marginLeft={optimisticContainer.containerMarginLeft}
                    
                    // Margin (Mobile) - ⚡ OPTIMISTIC
                    marginTopMobile={optimisticContainer.containerMarginTopMobile}
                    marginRightMobile={optimisticContainer.containerMarginRightMobile}
                    marginBottomMobile={optimisticContainer.containerMarginBottomMobile}
                    marginLeftMobile={optimisticContainer.containerMarginLeftMobile}
                    
                    // Background (Desktop)
                    backgroundColor={optimisticContainer.containerBackgroundColor}
                    backgroundColorOpacity={optimisticContainer.containerBackgroundColorOpacity}
                    backdropFilterBlur={optimisticContainer.containerBackdropFilterBlur}
                    backgroundGradientEnabled={optimisticContainer.containerBackgroundGradientEnabled}
                    backgroundGradientColor1={optimisticContainer.containerBackgroundGradientColor1}
                    backgroundGradientColor2={optimisticContainer.containerBackgroundGradientColor2}
                    backgroundGradientDirection={optimisticContainer.containerBackgroundGradientDirection}
                    backgroundImage={optimisticContainer.containerBackgroundImage}
                    backgroundImageSize={optimisticContainer.containerBackgroundImageSize}
                    backgroundImageScale={optimisticContainer.containerBackgroundImageScale}
                    backgroundImagePosition={optimisticContainer.containerBackgroundImagePosition}
                    backgroundImageOpacity={optimisticContainer.containerBackgroundImageOpacity}
                    
                    // ✅ Background (Mobile)
                    backgroundColorMobile={optimisticContainer.containerBackgroundColorMobile}
                    backgroundColorOpacityMobile={optimisticContainer.containerBackgroundColorOpacityMobile}
                    backdropFilterBlurMobile={optimisticContainer.containerBackdropFilterBlurMobile}
                    backgroundGradientEnabledMobile={optimisticContainer.containerBackgroundGradientEnabledMobile}
                    backgroundGradientColor1Mobile={optimisticContainer.containerBackgroundGradientColor1Mobile}
                    backgroundGradientColor2Mobile={optimisticContainer.containerBackgroundGradientColor2Mobile}
                    backgroundGradientDirectionMobile={optimisticContainer.containerBackgroundGradientDirectionMobile}
                    backgroundImageMobile={optimisticContainer.containerBackgroundImageMobile}
                    backgroundImageSizeMobile={optimisticContainer.containerBackgroundImageSizeMobile}
                    backgroundImageScaleMobile={optimisticContainer.containerBackgroundImageScaleMobile}
                    backgroundImagePositionMobile={optimisticContainer.containerBackgroundImagePositionMobile}
                    backgroundImageOpacityMobile={optimisticContainer.containerBackgroundImageOpacityMobile}
                    
                    // Border - ⚡ OPTIMISTIC
                    borderEnabled={optimisticContainer.containerBorderEnabled}
                    borderWidth={optimisticContainer.containerBorderWidth}
                    borderColor={optimisticContainer.containerBorderColor}
                    borderRadius={optimisticContainer.containerBorderRadius}
                    borderRadiusMobile={optimisticContainer.containerBorderRadiusMobile}
                    
                    // Shadow - ⚡ OPTIMISTIC
                    shadowEnabled={optimisticContainer.containerShadowEnabled}
                    shadowIntensity={optimisticContainer.containerShadowIntensity}
                    shadowDistance={optimisticContainer.containerShadowDistance}
                    shadowOpacity={optimisticContainer.containerShadowOpacity}
                    
                    // Alignment
                    contentAlignmentVertical={optimisticContainer.containerContentAlignmentVertical}
                    contentAlignmentHorizontal={optimisticContainer.containerContentAlignmentHorizontal}
                    
                    // Dimensions - ⚡ OPTIMISTIC
                    containerWidth={optimisticContainer.containerWidth}
                    containerHeight={optimisticContainer.containerHeight}
                    containerWidthMobile={optimisticContainer.containerWidthMobile}
                    containerHeightMobile={optimisticContainer.containerHeightMobile}
                    
                    // Callbacks
                    onStyleChange={(updates, isSliderUpdate = false) => {
                        console.log('🎨 Container Toolbar Updates:', { updates, isSliderUpdate });
                        
                        // Map updates zurück zu container prefix
                        const mappedUpdates = {};
                        Object.keys(updates).forEach(key => {
                            // Prefix "container" nur wenn nicht bereits vorhanden
                            const mappedKey = key.startsWith('container') ? key : `container${key.charAt(0).toUpperCase()}${key.slice(1)}`;
                            mappedUpdates[mappedKey] = updates[key];
                        });
                        
                        console.log('🎨 Mapped Updates:', mappedUpdates);
                        const updatedContainer = { ...container, ...mappedUpdates };
                        console.log('🎨 Updated Container:', updatedContainer);
                        handleContainerUpdate(updatedContainer, isSliderUpdate);
                    }}
                    
                    colors={colors}
                    isMobileLayout={isMobileLayout}
                />
                );
            })}
            


                {/* Logo Overlay - Vereinfacht und robust */}
                {data.showLogo && data.logoPositionMode === 'absolute' && (
                    <div style={{
                        position: 'absolute',
                        ...getLogoPositionStyles(data),
                        padding: '0px',
                        zIndex: 10
                    }}>
                        <div style={{ position: 'relative', display: 'inline-block' }}
                            onMouseEnter={(e) => {
                                const uploadBtn = e.currentTarget.querySelector('.logo-upload-btn');
                                if (uploadBtn) uploadBtn.style.opacity = '1';
                            }}
                            onMouseLeave={(e) => {
                                const uploadBtn = e.currentTarget.querySelector('.logo-upload-btn');
                                if (uploadBtn) uploadBtn.style.opacity = '0';
                            }}
                        >
                            <img 
                                src={data.logoImage || defaultLogo}
                                alt="Logo"
                                style={{
                                    width: appendPx(isMobileLayout ? (data.logoWidthMobile || data.logoWidth || 120) : (data.logoWidth || 120)),
                                    height: appendPx(data.logoHeight || 60),
                                    objectFit: 'contain',
                                    filter: data.logoInvert ? 'invert(1)' : 'none'
                                }}
                            />
                            {isEditMode && (
                                <button
                                    onClick={() => handleImageUpload('logoImage', setIsUploadingLogo)}
                                    style={{
                                        position: 'absolute',
                                        top: '-10px',
                                        left: '-10px',
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        background: 'var(--blue)',
                                        color: 'white',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: 0,
                                        transition: 'all 0.2s ease',
                                        zIndex: 100,
                                        boxShadow: '0 1px 3px rgba(99, 102, 241, 0.3)'
                                    }}
                                    className="logo-upload-btn"
                                >
                                    {isUploadingLogo ? (
                                        <div 
                                            style={{
                                                width: '16px',
                                                height: '16px',
                                                border: '2px solid white',
                                                borderTop: '2px solid transparent',
                                                borderRadius: '50%',
                                                animation: 'heroImageUploadSpin 1s linear infinite'
                                            }} 
                                        />
                                    ) : (
                                        <img 
                                            src="/img/upload.svg" 
                                            alt="Upload" 
                                            style={{
                                                width: '18px',
                                                height: '18px',
                                                filter: 'invert(1)'
                                            }}
                                        />
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                )}

              {/* 🚀 ABSOLUTE LOGO - Relativ zum Content-Container */}
              {data.showLogo && data.logoPositionMode === 'absolute' && 
               (!isMobileLayout || (isMobileLayout && data.showLogoMobile !== false)) && (
                <div style={{
                    position: 'absolute',
                    ...getLogoPositionStyles(data),
                    padding: '0px',
                    zIndex: 15
                }}>
                    <div style={{ position: 'relative', display: 'inline-block' }}
                        onMouseEnter={(e) => {
                            if (isEditMode) {
                                const uploadBtn = e.currentTarget.querySelector('.logo-upload-btn');
                                if (uploadBtn) uploadBtn.style.opacity = '1';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (isEditMode) {
                                const uploadBtn = e.currentTarget.querySelector('.logo-upload-btn');
                                if (uploadBtn) uploadBtn.style.opacity = '0';
                            }
                        }}
                    >
                        <img 
                            src={data.logoImage || defaultLogo}
                            alt="Logo"
                            style={{
                                width: appendPx(isMobileLayout ? (data.logoWidthMobile || data.logoWidth || 120) : (data.logoWidth || 120)),
                                height: appendPx(data.logoHeight || 60),
                                objectFit: 'contain',
                                filter: isMobileLayout ? 
                                    (data.logoInvertMobile !== undefined ? (data.logoInvertMobile ? 'invert(1)' : 'none') : (data.logoInvert ? 'invert(1)' : 'none')) :
                                    (data.logoInvert ? 'invert(1)' : 'none')
                            }}
                        />
                        {isEditMode && (
                            <button
                                onClick={() => handleImageUpload('logoImage', setIsUploadingLogo)}
                                style={{
                                    position: 'absolute',
                                    top: '-10px',
                                    left: '-10px',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    background: 'var(--blue)',
                                    color: 'white',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 0,
                                    transition: 'all 0.2s ease',
                                    zIndex: 100,
                                    boxShadow: '0 1px 3px rgba(99, 102, 241, 0.3)'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.background = 'var(--blue)';
                                    e.target.style.transform = 'scale(1.1)';
                                    e.target.style.boxShadow = '0 2px 6px rgba(99, 102, 241, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = 'var(--blue)';
                                    e.target.style.transform = 'scale(1)';
                                    e.target.style.boxShadow = '0 1px 3px rgba(99, 102, 241, 0.3)';
                                }}
                                title="Upload Logo"
                                className="logo-upload-btn"
                            >
                                {isUploadingLogo ? (
                                    <div 
                                        className="logo-upload-spinner"
                                        style={{
                                            width: '16px',
                                            height: '16px',
                                            border: '2px solid white',
                                            borderTop: '2px solid transparent',
                                            borderRadius: '50%',
                                            animation: 'heroImageUploadSpin 1s linear infinite'
                                        }} 
                                    />
                                ) : (
                                    <img 
                                        src="/img/upload.svg" 
                                        alt="Upload" 
                                        style={{
                                            width: '18px',
                                            height: '18px',
                                            filter: 'invert(1)'
                                        }}
                                    />
                                )}
                            </button>
                        )}
                    </div>
                </div>
              )}
            </div>

            {/* 🎨 LAYOUT FLOATING TOOLBAR - Widget Layout Settings */}
            <LayoutFloatingToolbar
                isEditMode={isEditMode}
                isVisible={isLayoutToolbarVisible}
                
                // 🎨 Background Props
                backgroundColor={data.backgroundColor || '#ffffff'}
                backgroundOpacity={data.backgroundOpacity ?? 100}
                backgroundGradientEnabled={data.backgroundGradientEnabled || false}
                backgroundGradientColor1={data.backgroundGradientColor1 || '#667eea'}
                backgroundGradientColor2={data.backgroundGradientColor2 || '#764ba2'}
                backgroundGradientAnimation={data.backgroundGradientAnimation || false}
                backgroundGradientDirection={data.backgroundGradientDirection || 'horizontal'}
                backgroundImage={data.backgroundImage || null}
                backgroundImageOpacity={data.backgroundImageOpacity ?? 100}
                backgroundImageSize={data.backgroundImageSize || 'cover'}
                backgroundImagePosition={data.backgroundImagePosition || 'center'}
                
                // Padding (Desktop)
                paddingTop={data.paddingTop ?? 0}
                paddingRight={data.paddingRight ?? 0}
                paddingBottom={data.paddingBottom ?? 0}
                paddingLeft={data.paddingLeft ?? 0}
                
                // Padding (Mobile)
                paddingTopMobile={data.paddingTopMobile}
                paddingRightMobile={data.paddingRightMobile}
                paddingBottomMobile={data.paddingBottomMobile}
                paddingLeftMobile={data.paddingLeftMobile}
                
                // Margin (Desktop)
                marginTop={data.marginTop ?? 0}
                marginRight={data.marginRight ?? 0}
                marginBottom={data.marginBottom ?? 0}
                marginLeft={data.marginLeft ?? 0}
                
                // Margin (Mobile)
                marginTopMobile={data.marginTopMobile}
                marginRightMobile={data.marginRightMobile}
                marginBottomMobile={data.marginBottomMobile}
                marginLeftMobile={data.marginLeftMobile}
                
                // Dimensions (Desktop)
                width={data.width}
                height={data.height}
                minWidth={data.minWidth}
                minHeight={data.minHeight}
                
                // Dimensions (Mobile)
                widthMobile={data.widthMobile}
                heightMobile={data.heightMobile}
                minWidthMobile={data.minWidthMobile}
                minHeightMobile={data.minHeightMobile}
                
                // Layout Settings (Desktop)
                flexDirection={data.flexDirection || 'row'}
                childDistribution={data.childDistribution || '50-50'}
                
                // Layout Settings (Mobile)
                flexDirectionMobile={data.flexDirectionMobile}
                childDistributionMobile={data.childDistributionMobile}
                
                gridLayout={workingData.gridLayout || 'grid-cols-2-equal'}
                containerCount={containerSections.length}
                
                // 🎨 Border Settings
                borderTopEnabled={data.borderTopEnabled || false}
                borderTopWidth={data.borderTopWidth || 1}
                borderTopColor={data.borderTopColor || '#cccccc'}
                borderBottomEnabled={data.borderBottomEnabled || false}
                borderBottomWidth={data.borderBottomWidth || 1}
                borderBottomColor={data.borderBottomColor || '#cccccc'}
                borderLeftEnabled={data.borderLeftEnabled || false}
                borderLeftWidth={data.borderLeftWidth || 1}
                borderLeftColor={data.borderLeftColor || '#cccccc'}
                borderRightEnabled={data.borderRightEnabled || false}
                borderRightWidth={data.borderRightWidth || 1}
                borderRightColor={data.borderRightColor || '#cccccc'}
                
                // Callbacks
                onStyleChange={(updates) => {
                    // Merge updates direkt in workingData
                    onUpdate({ ...workingData, ...updates });
                }}
                colors={colors}
                onClose={() => setCurrentlyOpenToolbar(null)}
                
                // Additional Props
                widgetRef={widgetRef}
                
                // Responsive
                isMobileLayout={isMobileLayout}
            />
            
            {/* 🤖 AI Prompt Popup */}
            <AIPromptPopup
                isOpen={showAIPromptPopup}
                onClose={() => setShowAIPromptPopup(false)}
                onSubmit={(userPrompt) => handleAIRefresh(userPrompt)}
                widgetType="Hero Header"
                contextInfo={spaceFonts?.contextInfo || ''}
                isLoading={isAIRefreshing}
            />
        </>
    );
}

export default HeroHeader;
